import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Skeleton } from "@app/components/shared/Skeleton";
import { SectionHeader } from "@app/components/shared/SectionHeader";
import { AdminPositionsMonitorTab } from "./AdminPositionsMonitorTab";
import { SubscriberPositionsTab } from "./SubscriberPositionsTab";
import { ExecutionDiagnosticsTab } from "./ExecutionDiagnosticsTab";
import {
  getRecentExitSignals,
  getExitSignalStatus,
  retryExitJobs,
  forceClosePositions,
  getStuckJobs,
  cleanupCompletedPositions,
  cleanupAllOldData,
  comprehensiveCleanup,
  previewCleanup,
  operationalResidueCleanup,
  previewOperationalResidueCleanup,
  type CleanupPreviewResponse,
  type OperationalResidueCleanupResponse,
  deleteStuckJob,
  deleteAllStuckJobs,
  getAllPendingJobs,
  deleteAllPendingJobs,
  deleteJobById,
  getPendingOrders,
  cancelPendingOrder,
  cancelAllPendingOrders,
  getAllStuckPositions,
  archiveExitSignals,
  archiveExitSignalsWithForce,
  getArchivedExitSignals,
  archiveStuckExitSignals,
  deleteStuckPosition,
  getOldTestPositions,
  deleteOldTestPosition,
  deleteAllOldTestPositions,
  getPendingSignals,
  deletePendingSignal,
  deleteAllPendingSignals,
  deletePositionsBySignal,
  deleteOldExitSignalsAndPositions,
  getRedisStreamStats,
  trimRedisStreams,
  purgeRedisStream,
  type StreamStatsResponse,
  type StreamPurgeRequest,
  type DeletePositionBySignalRequest,
  type ArchiveStuckExitSignalsRequest,
  type DeleteStuckPositionRequest,
  type RetryExitJobsRequest,
  type ForceClosePositionsRequest,
  type ArchiveExitSignalsRequest,
  type ArchiveExitSignalsResponse,
} from "@app/services/adminExitSignalsApi";
import { logger } from "@app/services/logger";
import "./admin-exit-signals.css";

const EXIT_SIGNALS_QUERY_KEY = ["admin", "exit-signals", "recent"];

type InternalTab = "positions-monitor" | "signals" | "pending-orders" | "stuck-jobs" | "archived" | "cleanup-operations" | "subscriber-positions" | "execution-diagnostics";

// ============================================================================
// REDIS STREAM MANAGER COMPONENT (defined before use)
// ============================================================================

function RedisStreamManager(): JSX.Element {
  const [streamStats, setStreamStats] = useState<StreamStatsResponse | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isTrimming, setIsTrimming] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [lastAction, setLastAction] = useState<string>("");
  const [purgeKey, setPurgeKey] = useState("trading_system:exit_stream");
  const [purgeCG, setPurgeCG] = useState("exit_workers");

  const fetchStats = async () => {
    setIsLoadingStats(true);
    try {
      const stats = await getRedisStreamStats();
      setStreamStats(stats);
      setLastAction(`Stats fetched at ${new Date().toLocaleTimeString()}`);
    } catch (error: any) {
      logger.error("Failed to fetch stream stats", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to fetch stats: ${error.message}`);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const handleTrim = async () => {
    if (!confirm("Trim Redis streams to MAXLEN?\n\nThis will:\n- Keep last 10k entries for entry/monitor streams\n- Keep last 5k for exit stream\n- Keep last 1k for DLQ\n\nSafe operation - does not affect pending jobs.")) {
      return;
    }
    setIsTrimming(true);
    try {
      const result = await trimRedisStreams();
      setLastAction(`Trimmed ${result.total_trimmed} entries at ${new Date().toLocaleTimeString("en-US")}`);
      alert(`Trim completed!\nTotal trimmed: ${result.total_trimmed}\nCumulative: ${result.cumulative_trimmed}`);
      // Refresh stats after trim
      await fetchStats();
    } catch (error: any) {
      logger.error("Failed to trim streams", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to trim: ${error.message}`);
    } finally {
      setIsTrimming(false);
    }
  };

  const handlePurge = async () => {
    if (!confirm(`⚠️ DESTRUCTIVE OPERATION ⚠️\n\nDelete entire stream: ${purgeKey}?\n\nThis will:\n- DELETE ALL entries in the stream\n- Lose ALL pending jobs in this stream\n- Recreate consumer group: ${purgeCG || "none"}\n\nOnly use for emergency/corruption recovery!`)) {
      return;
    }
    if (!confirm(`Confirm again: Delete ${purgeKey} completely?`)) {
      return;
    }
    setIsPurging(true);
    try {
      const request: StreamPurgeRequest = {
        stream_key: purgeKey,
        consumer_group: purgeCG || undefined,
        confirm: true,
      };
      const result = await purgeRedisStream(request);
      setLastAction(`Purged ${result.stream_key} at ${new Date().toLocaleTimeString()}`);
      alert(`Purge result:\nDeleted: ${result.deleted}\nCG recreated: ${result.consumer_group_recreated}\nError: ${result.error || "none"}`);
      // Refresh stats after purge
      await fetchStats();
    } catch (error: any) {
      logger.error("Failed to purge stream", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to purge: ${error.message}`);
    } finally {
      setIsPurging(false);
    }
  };

  return (
    <div>
      {/* Stats Display */}
      <div style={{ marginBottom: "1rem" }}>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem", flexWrap: "wrap" }}>
          <Button 
            onClick={fetchStats} 
            disabled={isLoadingStats}
            variant="secondary"
          >
            {isLoadingStats ? "Loading..." : "Refresh Stats"}
          </Button>
          <Button 
            onClick={handleTrim} 
            disabled={isTrimming}
            variant="primary"
          >
            {isTrimming ? "Trimming..." : "Trim Streams (Safe)"}
          </Button>
        </div>
        {lastAction && (
          <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
            Last action: {lastAction}
          </div>
        )}
      </div>

      {/* Stream Stats Table */}
      {streamStats?.success && streamStats.streams && (
        <div style={{ marginBottom: "1rem", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--border)" }}>
                <th style={{ padding: "0.5rem", textAlign: "left" }}>Stream</th>
                <th style={{ padding: "0.5rem", textAlign: "right" }}>XLEN</th>
                <th style={{ padding: "0.5rem", textAlign: "right" }}>MaxLen</th>
                <th style={{ padding: "0.5rem", textAlign: "right" }}>Pending</th>
                <th style={{ padding: "0.5rem", textAlign: "left" }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(streamStats.streams).map(([key, stat]) => (
                <tr key={key} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "0.5rem", fontFamily: "monospace", fontSize: "0.8rem" }}>
                    {key.replace("trading_system:", "")}
                  </td>
                  <td style={{ padding: "0.5rem", textAlign: "right" }}>
                    {stat.xlen?.toLocaleString() || "N/A"}
                  </td>
                  <td style={{ padding: "0.5rem", textAlign: "right" }}>
                    {stat.maxlen?.toLocaleString() || "N/A"}
                  </td>
                  <td style={{ padding: "0.5rem", textAlign: "right" }}>
                    {stat.pending?.toLocaleString() ?? "N/A"}
                  </td>
                  <td style={{ padding: "0.5rem" }}>
                    {stat.error ? (
                      <span style={{ color: "var(--error)" }}>Error: {stat.error}</span>
                    ) : (
                      <span style={{ 
                        color: stat.xlen > stat.maxlen ? "var(--warning)" : "var(--success)"
                      }}>
                        {stat.xlen > stat.maxlen ? "Over limit" : "OK"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Purge Section (Emergency) */}
      <div style={{ 
        marginTop: "1rem", 
        padding: "1rem", 
        border: "1px solid var(--error)", 
        borderRadius: "0.5rem",
        backgroundColor: "rgba(220, 53, 69, 0.05)"
      }}>
        <h4 style={{ color: "var(--error)", marginTop: 0, marginBottom: "0.5rem" }}>
          ⚠️ Emergency Purge (Destructive)
        </h4>
        <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "0.75rem" }}>
          Only use when stream is corrupted or unrecoverable. This deletes ALL data in the stream.
        </p>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          <select
            value={purgeKey}
            onChange={(e) => setPurgeKey(e.target.value)}
            style={{ padding: "0.25rem" }}
          >
            <option value="trading_system:exit_stream">trading_system:exit_stream</option>
            <option value="trading_system:entry_stream">trading_system:entry_stream</option>
            <option value="trading_system:monitor_stream">trading_system:monitor_stream</option>
            <option value="trading_system:execution_dlq">trading_system:execution_dlq</option>
          </select>
          <input
            type="text"
            value={purgeCG}
            onChange={(e) => setPurgeCG(e.target.value)}
            placeholder="Consumer group (optional)"
            style={{ padding: "0.25rem", width: "150px" }}
          />
          <Button
            onClick={handlePurge}
            disabled={isPurging}
            variant="danger"
          >
            {isPurging ? "Purging..." : "PURGE Stream"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function renderProtectedPositionsBreakdown(summary: Record<string, unknown>): JSX.Element | null {
  const rawBreakdown = summary["live_positions_breakdown"];
  if (!rawBreakdown || typeof rawBreakdown !== "object" || Array.isArray(rawBreakdown)) {
    return null;
  }
  const breakdown = rawBreakdown as Record<string, unknown>;
  const rawSources =
    breakdown["sources"] && typeof breakdown["sources"] === "object" && !Array.isArray(breakdown["sources"])
      ? (breakdown["sources"] as Record<string, unknown>)
      : {};
  const bySymbol = Array.isArray(breakdown["by_symbol"])
    ? (breakdown["by_symbol"] as Array<Record<string, unknown>>)
    : [];
  const symbols = Array.isArray(breakdown["symbols"])
    ? (breakdown["symbols"] as string[])
    : [];

  return (
    <div style={{ marginBottom: "1rem", padding: "0.75rem", backgroundColor: "white", borderRadius: "4px" }}>
      <h5 style={{ margin: "0 0 0.75rem 0", color: "#1e3a8a" }}>Protected Live Positions</h5>
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "0.75rem", fontSize: "0.875rem" }}>
        <span><strong>Total positions:</strong> {String(breakdown["total_unique_positions"] ?? 0)}</span>
        <span><strong>Total symbols:</strong> {String(breakdown["total_unique_symbols"] ?? 0)}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.75rem", marginBottom: "0.75rem" }}>
        {Object.entries(rawSources).map(([sourceName, sourceValue]) => {
          const source =
            sourceValue && typeof sourceValue === "object" && !Array.isArray(sourceValue)
              ? (sourceValue as Record<string, unknown>)
              : {};
          const sourceSymbols = Array.isArray(source["symbols"]) ? (source["symbols"] as string[]) : [];
          return (
            <div key={sourceName} style={{ padding: "0.75rem", border: "1px solid #bfdbfe", borderRadius: "4px" }}>
              <div style={{ fontWeight: 700, marginBottom: "0.35rem" }}>{sourceName}</div>
              <div style={{ fontSize: "0.875rem", marginBottom: "0.35rem" }}>
                Count: {String(source["count"] ?? 0)}
              </div>
              <div style={{ fontSize: "0.8rem", color: "#1e3a8a", wordBreak: "break-word" }}>
                Symbols: {sourceSymbols.length > 0 ? sourceSymbols.join(", ") : "None"}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: "0.85rem", marginBottom: "0.75rem", color: "#1e3a8a", wordBreak: "break-word" }}>
        <strong>All symbols:</strong> {symbols.length > 0 ? symbols.join(", ") : "None"}
      </div>
      {bySymbol.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #bfdbfe" }}>
                <th style={{ textAlign: "left", padding: "0.35rem" }}>Symbol</th>
                <th style={{ textAlign: "left", padding: "0.35rem" }}>Sources</th>
                <th style={{ textAlign: "right", padding: "0.35rem" }}>UserPosition</th>
                <th style={{ textAlign: "right", padding: "0.35rem" }}>Redis</th>
                <th style={{ textAlign: "right", padding: "0.35rem" }}>TradeLog</th>
              </tr>
            </thead>
            <tbody>
              {bySymbol.map((item) => {
                const sourceList = Array.isArray(item["sources"]) ? (item["sources"] as string[]) : [];
                return (
                  <tr key={String(item["symbol"])} style={{ borderBottom: "1px solid #dbeafe" }}>
                    <td style={{ padding: "0.35rem", fontWeight: 600 }}>{String(item["symbol"] ?? "")}</td>
                    <td style={{ padding: "0.35rem" }}>{sourceList.join(", ") || "None"}</td>
                    <td style={{ padding: "0.35rem", textAlign: "right" }}>{String(item["user_positions"] ?? 0)}</td>
                    <td style={{ padding: "0.35rem", textAlign: "right" }}>{String(item["redis_active_positions"] ?? 0)}</td>
                    <td style={{ padding: "0.35rem", textAlign: "right" }}>{String(item["trade_log"] ?? 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN ADMIN EXIT SIGNALS TAB COMPONENT
// ============================================================================

export function AdminExitSignalsTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [selectedSignalId, setSelectedSignalId] = useState<string | null>(null);
  const [showRetryConfirm, setShowRetryConfirm] = useState<string | null>(null);
  const [showForceCloseConfirm, setShowForceCloseConfirm] = useState<string | null>(null);
  const [selectedInternalTab, setSelectedInternalTab] = useState<InternalTab>("positions-monitor");
  const [previewData, setPreviewData] = useState<CleanupPreviewResponse | null>(null);
  const [previewCleanupError, setPreviewCleanupError] = useState<string | null>(null);
  const [residuePreviewData, setResiduePreviewData] = useState<OperationalResidueCleanupResponse | null>(null);
  const [residueCleanupError, setResidueCleanupError] = useState<string | null>(null);

  const {
    data: recentSignals,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: EXIT_SIGNALS_QUERY_KEY,
    queryFn: () => getRecentExitSignals(24, 5, 50),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const {
    data: signalStatus,
    isLoading: statusLoading,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ["admin", "exit-signals", "status", selectedSignalId],
    queryFn: () => {
      if (!selectedSignalId) {
        throw new Error("Signal ID is required");
      }
      return getExitSignalStatus(selectedSignalId);
    },
    enabled: !!selectedSignalId,
  });
  
  // CRITICAL FIX: Refetch status when selectedSignalId changes
  useEffect(() => {
    if (selectedSignalId) {
      refetchStatus();
    }
  }, [selectedSignalId, refetchStatus]);

  const retryMutation = useMutation({
    mutationFn: ({ signalId, request }: { signalId: string; request: RetryExitJobsRequest }) =>
      retryExitJobs(signalId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXIT_SIGNALS_QUERY_KEY });
      if (selectedSignalId) {
        refetchStatus();
      }
      setShowRetryConfirm(null);
      alert("Exit jobs retried successfully");
    },
    onError: (error: any) => {
      logger.error("Failed to retry exit jobs", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to retry exit jobs: ${error.message || "Unknown error"}`);
    },
  });

  const forceCloseMutation = useMutation({
    mutationFn: ({
      signalId,
      request,
    }: {
      signalId: string;
      request: ForceClosePositionsRequest;
    }) => forceClosePositions(signalId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EXIT_SIGNALS_QUERY_KEY });
      if (selectedSignalId) {
        refetchStatus();
      }
      setShowForceCloseConfirm(null);
      alert("Positions force closed successfully");
    },
    onError: (error: any) => {
      logger.error("Failed to force close positions", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to force close positions: ${error.message || "Unknown error"}`);
    },
  });

  // CRITICAL FIX: Move all hooks before early returns to prevent React Hooks order violation
  const {
    data: stuckJobs,
    refetch: refetchStuckJobs,
  } = useQuery({
    queryKey: ["admin", "exit-signals", "stuck-jobs"],
    queryFn: () => getStuckJobs(10),
    enabled: selectedInternalTab === "stuck-jobs",
    refetchInterval: selectedInternalTab === "stuck-jobs" ? 30000 : false,
  });

  const {
    data: allPendingJobs,
    isLoading: allPendingJobsLoading,
    refetch: refetchAllPendingJobs,
  } = useQuery({
    queryKey: ["admin", "exit-signals", "pending-jobs-all"],
    queryFn: getAllPendingJobs,
    enabled: selectedInternalTab === "stuck-jobs",
    refetchInterval: selectedInternalTab === "stuck-jobs" ? 30000 : false,
  });

  const {
    data: allStuckPositions,
    isLoading: allStuckPositionsLoading,
    refetch: refetchAllStuckPositions,
  } = useQuery({
    queryKey: ["admin", "exit-signals", "all-stuck-positions"],
    queryFn: getAllStuckPositions,
    enabled: selectedInternalTab === "stuck-jobs",
    refetchInterval: selectedInternalTab === "stuck-jobs" ? 30000 : false,
  });

  const cleanupCompletedMutation = useMutation({
    mutationFn: (daysOld: number) => cleanupCompletedPositions(daysOld),
    onSuccess: (data) => {
      alert(`Successfully cleaned up ${data.deleted} completed positions older than specified days`);
    },
    onError: (error: any) => {
      logger.error("Failed to cleanup completed positions", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to cleanup completed positions: ${error.message || "Unknown error"}`);
    },
  });

  const cleanupAllOldDataMutation = useMutation({
    mutationFn: ({ daysOld, includePending }: { daysOld: number; includePending: boolean }) =>
      cleanupAllOldData(daysOld, includePending),
    onSuccess: (data) => {
      alert(`Successfully cleaned up ${data.deleted} total entries from all storage locations`);
    },
    onError: (error: any) => {
      logger.error("Failed to cleanup all old data", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to cleanup all old data: ${error.message || "Unknown error"}`);
    },
  });

  const comprehensiveCleanupMutation = useMutation({
    mutationFn: ({
      daysOld,
      includePending,
      wipeAll,
    }: {
      daysOld: number;
      includePending: boolean;
      wipeAll?: boolean;
    }) => comprehensiveCleanup(daysOld, includePending, wipeAll ?? false),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: EXIT_SIGNALS_QUERY_KEY });
      const summaryStr = data.summary ? JSON.stringify(data.summary, null, 2) : "";
      alert(
        `Full cleanup completed.\nTotal deleted: ${data.total_deleted}\n\nDetails:\n${summaryStr}`
      );
    },
    onError: (error: any) => {
      logger.error("Failed to run comprehensive cleanup", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to run comprehensive cleanup: ${error.message || "Unknown error"}`);
    },
  });

  const previewCleanupMutation = useMutation({
    mutationFn: ({
      daysOld,
      includePending,
      wipeAll,
    }: {
      daysOld: number;
      includePending: boolean;
      wipeAll?: boolean;
    }) => previewCleanup(daysOld, includePending, wipeAll ?? false),
    onSuccess: (data) => {
      setPreviewData(data);
      setPreviewCleanupError(null);
    },
    onError: (error: any) => {
      setPreviewData(null);
      setPreviewCleanupError(error?.message || "Preview failed");
      logger.error("Failed to preview cleanup", error instanceof Error ? error : new Error(String(error)));
    },
  });

  const previewResidueCleanupMutation = useMutation({
    mutationFn: ({
      maxAgeHours,
      streamScanCount,
    }: {
      maxAgeHours: number;
      streamScanCount: number;
    }) => previewOperationalResidueCleanup(maxAgeHours, streamScanCount),
    onSuccess: (data) => {
      setResiduePreviewData(data);
      setResidueCleanupError(null);
    },
    onError: (error: any) => {
      setResiduePreviewData(null);
      setResidueCleanupError(error?.message || "Preview failed");
      logger.error("Failed to preview residue cleanup", error instanceof Error ? error : new Error(String(error)));
    },
  });

  const residueCleanupMutation = useMutation({
    mutationFn: ({
      maxAgeHours,
      streamScanCount,
    }: {
      maxAgeHours: number;
      streamScanCount: number;
    }) => operationalResidueCleanup(maxAgeHours, streamScanCount),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: EXIT_SIGNALS_QUERY_KEY });
      setResiduePreviewData(data);
      setResidueCleanupError(null);
      const summaryStr = data.summary ? JSON.stringify(data.summary, null, 2) : "";
      alert(
        `Operational residue cleanup completed.\n` +
          `Trade history protected: ${data.history_protected ? "yes" : "no"}\n` +
          `Total deleted: ${data.total_deleted ?? 0}\n\nDetails:\n${summaryStr}`
      );
    },
    onError: (error: any) => {
      logger.error("Failed to execute residue cleanup", error instanceof Error ? error : new Error(String(error)));
      setResidueCleanupError(error?.message || "Execution failed");
      alert(`Failed to execute residue cleanup: ${error.message || "Unknown error"}`);
    },
  });

  const deleteOldExitSignalsMutation = useMutation({
    mutationFn: (maxAgeHours: number) => deleteOldExitSignalsAndPositions(maxAgeHours),
    onSuccess: async (data) => {
      const deletedCount = data?.deleted || 0;
      const errors = data?.errors || [];
      
      // Wait a bit for backend to complete deletion
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: EXIT_SIGNALS_QUERY_KEY });
      await refetch();
      
      if (errors.length > 0) {
        alert(`تم حذف ${deletedCount} صفقة. الأخطاء: ${errors.join(", ")}\nDeleted ${deletedCount} positions. Errors: ${errors.join(", ")}`);
      } else if (deletedCount > 0) {
        alert(`تم حذف ${deletedCount} صفقة بنجاح\nSuccessfully deleted ${deletedCount} position(s)`);
      } else {
        alert(`لم يتم حذف أي صفقات. قد لا توجد إشارات قديمة.\nNo positions deleted. No old signals found.`);
      }
    },
    onError: (error: any) => {
      logger.error("Failed to delete old exit signals", error instanceof Error ? error : new Error(String(error)));
      alert(`فشل حذف الإشارات القديمة: ${error.message || "Unknown error"}\nFailed to delete old exit signals: ${error.message || "Unknown error"}`);
    },
  });

  // ============================================================================
  const deleteStuckJobMutation = useMutation({
    mutationFn: deleteStuckJob,
    onSuccess: async (response) => {
      logger.info("Delete stuck job response:", { ...response });
      const deletedCount = response?.deleted || 0;
      const errors = response?.errors || [];
      
      // Wait a bit for backend to complete deletion
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "stuck-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "all-stuck-positions"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "pending-jobs-all"] });
      await Promise.all([
        refetchStuckJobs(),
        refetchAllStuckPositions(),
        refetchAllPendingJobs(),
      ]);
      
      if (errors.length > 0) {
        alert(`Stuck job deletion completed with ${deletedCount} deleted. Errors: ${errors.join(", ")}`);
      } else if (deletedCount > 0) {
        alert(`Stuck job deleted successfully (${deletedCount} items deleted)`);
      } else {
        alert(`No items were deleted. Job may not exist or was already deleted.`);
      }
    },
    onError: (error: any) => {
      logger.error("Failed to delete stuck job", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to delete stuck job: ${error.message || "Unknown error"}`);
    },
  });

  const deleteAllStuckJobsMutation = useMutation({
    mutationFn: () => deleteAllStuckJobs(10),
    onSuccess: async (response) => {
      logger.info("Delete all stuck jobs response:", { ...response });
      const deletedCount = response?.deleted || 0;
      const errors = response?.errors || [];
      
      // Wait a bit for backend to complete deletion
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "stuck-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "all-stuck-positions"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "pending-jobs-all"] });
      await Promise.all([
        refetchStuckJobs(),
        refetchAllStuckPositions(),
        refetchAllPendingJobs(),
      ]);
      
      if (errors.length > 0) {
        alert(`All stuck jobs deletion completed with ${deletedCount} deleted. Errors: ${errors.join(", ")}`);
      } else if (deletedCount > 0) {
        alert(`All stuck jobs deleted successfully (${deletedCount} items deleted)`);
      } else {
        alert(`No items were deleted. Jobs may not exist or were already deleted.`);
      }
    },
    onError: (error: any) => {
      logger.error("Failed to delete all stuck jobs", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to delete all stuck jobs: ${error.message || "Unknown error"}`);
    },
  });

  const deleteAllPendingJobsMutation = useMutation({
    mutationFn: deleteAllPendingJobs,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "stuck-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "all-stuck-positions"] });
      refetchStuckJobs();
      refetchAllStuckPositions();
      alert(`Successfully deleted ${data.deleted} pending jobs from database${data.errors.length > 0 ? `. Errors: ${data.errors.join(", ")}` : ""}`);
    },
    onError: (error: any) => {
      logger.error("Failed to delete all pending jobs", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to delete all pending jobs: ${error.message || "Unknown error"}`);
    },
  });

  const deleteJobByIdMutation = useMutation({
    mutationFn: deleteJobById,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "stuck-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "all-stuck-positions"] });
      refetchStuckJobs();
      refetchAllStuckPositions();
      alert("Job deleted successfully");
    },
    onError: (error: any) => {
      logger.error("Failed to delete job by id", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to delete job: ${error.message || "Unknown error"}`);
    },
  });

  const archiveStuckExitSignalsMutation = useMutation({
    mutationFn: (request: ArchiveStuckExitSignalsRequest) =>
      archiveStuckExitSignals(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: EXIT_SIGNALS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "stuck-jobs"] });
      if (selectedSignalId) {
        refetchStatus();
      }
      const message = data.dry_run
        ? `Dry run completed: Found ${data.signals_found} stuck signal(s). Would archive ${data.signals_archived} signal(s) and delete ${data.execution_jobs_deleted} ExecutionJob(s).`
        : `Successfully archived ${data.signals_archived} stuck exit signal(s) and deleted ${data.execution_jobs_deleted} ExecutionJob(s).`;
      alert(message);
      if (data.errors.length > 0) {
        alert(`Errors encountered: ${data.errors.join(", ")}`);
      }
    },
    onError: (error: any) => {
      logger.error("Failed to archive stuck exit signals", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to archive stuck exit signals: ${error.message || "Unknown error"}`);
    },
  });

  const deleteStuckPositionMutation = useMutation({
    mutationFn: (request: DeleteStuckPositionRequest) =>
      deleteStuckPosition(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: EXIT_SIGNALS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "stuck-jobs"] });
      if (selectedSignalId) {
        refetchStatus();
      }
      const foundSummary = `Found: ${data.found.execution_jobs} ExecutionJob(s), ${data.found.signal_events} SignalEvent(s), ${data.found.user_positions} UserPosition(s), ${data.found.trade_logs} TradeLog(s), ${data.found.exit_tickets} ExitTicket(s), ${data.found.redis_positions} Redis position(s), ${data.found.redis_stream_entries} Redis stream entry/entries`;
      const deletedSummary = `Deleted: ${data.deleted.execution_jobs} ExecutionJob(s), ${data.deleted.signal_events} SignalEvent(s), ${data.deleted.user_positions} UserPosition(s), ${data.deleted.trade_logs} TradeLog(s), ${data.deleted.exit_tickets} ExitTicket(s), ${data.deleted.redis_positions} Redis position(s), ${data.deleted.redis_stream_entries} Redis stream entry/entries`;
      const message = data.dry_run
        ? `Dry run completed:\n${foundSummary}\n\nWould delete:\n${deletedSummary}`
        : `Successfully deleted stuck position:\n${foundSummary}\n\nDeleted:\n${deletedSummary}`;
      alert(message);
      if (data.errors.length > 0) {
        alert(`Errors encountered: ${data.errors.join("\n")}`);
      }
    },
    onError: (error: any) => {
      logger.error("Failed to delete stuck position", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to delete stuck position: ${error.message || "Unknown error"}`);
    },
  });

  const {
    data: pendingOrders,
    isLoading: pendingOrdersLoading,
    refetch: refetchPendingOrders,
  } = useQuery({
    queryKey: ["admin", "exit-signals", "pending-orders"],
    queryFn: getPendingOrders,
    enabled: selectedInternalTab === "pending-orders",
    refetchInterval: selectedInternalTab === "pending-orders" ? 30000 : false,
  });

  const cancelPendingOrderMutation = useMutation({
    mutationFn: cancelPendingOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "pending-orders"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "all-stuck-positions"] });
      refetchPendingOrders();
      refetchAllStuckPositions();
      alert("Pending order cancelled successfully");
    },
    onError: (error: any) => {
      logger.error("Failed to cancel pending order", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to cancel pending order: ${error.message || "Unknown error"}`);
    },
  });

  const cancelAllPendingOrdersMutation = useMutation({
    mutationFn: cancelAllPendingOrders,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "pending-orders"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "all-stuck-positions"] });
      refetchPendingOrders();
      refetchAllStuckPositions();
      alert("All pending orders cancelled successfully");
    },
    onError: (error: any) => {
      logger.error("Failed to cancel all pending orders", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to cancel all pending orders: ${error.message || "Unknown error"}`);
    },
  });

  // Old Test Positions queries and mutations
  const {
    data: oldTestPositions,
    isLoading: oldTestPositionsLoading,
    refetch: refetchOldTestPositions,
  } = useQuery({
    queryKey: ["admin", "exit-signals", "old-test-positions"],
    queryFn: getOldTestPositions,
    enabled: selectedInternalTab === "archived" || selectedInternalTab === "cleanup-operations",
    refetchInterval: selectedInternalTab === "archived" || selectedInternalTab === "cleanup-operations" ? 30000 : false,
  });

  const deleteOldTestPositionMutation = useMutation({
    mutationFn: deleteOldTestPosition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "old-test-positions"] });
      refetchOldTestPositions();
      alert("Old test position deleted successfully");
    },
    onError: (error: any) => {
      logger.error("Failed to delete old test position", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to delete old test position: ${error.message || "Unknown error"}`);
    },
  });

  const deleteAllOldTestPositionsMutation = useMutation({
    mutationFn: deleteAllOldTestPositions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "old-test-positions"] });
      refetchOldTestPositions();
      alert("All old test positions deleted successfully");
    },
    onError: (error: any) => {
      logger.error("Failed to delete all old test positions", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to delete all old test positions: ${error.message || "Unknown error"}`);
    },
  });

  // Pending Signals queries and mutations
  const {
    data: pendingSignals,
    isLoading: pendingSignalsLoading,
    refetch: refetchPendingSignals,
  } = useQuery({
    queryKey: ["admin", "exit-signals", "pending-signals"],
    queryFn: getPendingSignals,
    enabled: selectedInternalTab === "signals",
    refetchInterval: selectedInternalTab === "signals" ? 30000 : false,
  });

  const deletePendingSignalMutation = useMutation({
    mutationFn: deletePendingSignal,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "pending-signals"] });
      refetchPendingSignals();
      alert("Pending signal deleted successfully");
    },
    onError: (error: any) => {
      logger.error("Failed to delete pending signal", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to delete pending signal: ${error.message || "Unknown error"}`);
    },
  });

  const deleteAllPendingSignalsMutation = useMutation({
    mutationFn: deleteAllPendingSignals,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "pending-signals"] });
      refetchPendingSignals();
      alert("All pending signals deleted successfully");
    },
    onError: (error: any) => {
      logger.error("Failed to delete all pending signals", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to delete all pending signals: ${error.message || "Unknown error"}`);
    },
  });

  const deletePositionBySignalMutation = useMutation({
    mutationFn: ({ signalId, request }: { signalId: string; request: DeletePositionBySignalRequest }) =>
      deletePositionsBySignal(signalId, request),
    onSuccess: async (response) => {
      logger.info("Delete position by signal response:", { ...response });
      const deletedCount = response?.deleted || 0;
      const errors = response?.errors || [];
      
      // Wait a bit for backend to complete deletion
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: EXIT_SIGNALS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "status", selectedSignalId] });
      await Promise.all([
        refetch(),
        refetchStatus(),
      ]);
      
      if (errors.length > 0) {
        alert(`Position deletion completed with ${deletedCount} deleted. Errors: ${errors.join(", ")}`);
      } else if (deletedCount > 0) {
        alert(`Position deleted successfully (${deletedCount} item(s) deleted)`);
      } else {
        alert(`No items were deleted. Position may not exist or was already deleted.`);
      }
    },
    onError: (error: any) => {
      logger.error("Failed to delete position by signal", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to delete position: ${error.message || "Unknown error"}`);
    },
  });

  // CRITICAL FIX: Early returns must come after all hooks to prevent React Hooks order violation
  if (isLoading) {
    return (
      <div className="admin-exit-signals-root">
        <Card title="Exit Signals Management">
          <Skeleton />
        </Card>
      </div>
    );
  }

  if (isError || !recentSignals) {
    return (
      <div className="admin-exit-signals-root">
        <Card title="Exit Signals Management">
          <div className="admin-exit-signals-error">
            Failed to load exit signals. Please try again.
          </div>
          <Button onClick={() => refetch()}>Retry</Button>
        </Card>
      </div>
    );
  }

  const signalsWithOpenPositions = recentSignals.signals.filter(
    (s) => s.users_with_open_positions.length > 0
  );

  return (
    <div className="admin-exit-signals-root">
      <SectionHeader
        title="Exit Signals Management"
        subtitle="Monitor and manage exit signals for subscribers who failed to exit after admin exit"
      />

      <div className="admin-exit-signals-internal-tabs">
        <button
          className={`admin-exit-signals-internal-tab ${
            selectedInternalTab === "positions-monitor" ? "active" : ""
          }`}
          onClick={() => setSelectedInternalTab("positions-monitor")}
        >
          Positions Monitor
        </button>
        <button
          className={`admin-exit-signals-internal-tab ${
            selectedInternalTab === "signals" ? "active" : ""
          }`}
          onClick={() => setSelectedInternalTab("signals")}
        >
          Exit Signals
        </button>
        <button
          className={`admin-exit-signals-internal-tab ${
            selectedInternalTab === "pending-orders" ? "active" : ""
          }`}
          onClick={() => setSelectedInternalTab("pending-orders")}
        >
          Pending Orders
        </button>
        <button
          className={`admin-exit-signals-internal-tab ${
            selectedInternalTab === "stuck-jobs" ? "active" : ""
          }`}
          onClick={() => setSelectedInternalTab("stuck-jobs")}
        >
          Stuck Jobs
        </button>
        <button
          className={`admin-exit-signals-internal-tab ${
            selectedInternalTab === "archived" ? "active" : ""
          }`}
          onClick={() => setSelectedInternalTab("archived")}
        >
          Archived
        </button>
        <button
          className={`admin-exit-signals-internal-tab ${
            selectedInternalTab === "cleanup-operations" ? "active" : ""
          }`}
          onClick={() => setSelectedInternalTab("cleanup-operations")}
        >
          Cleanup
        </button>
        <button
          className={`admin-exit-signals-internal-tab ${
            selectedInternalTab === "subscriber-positions" ? "active" : ""
          }`}
          onClick={() => setSelectedInternalTab("subscriber-positions")}
        >
          Subscribers
        </button>
        <button
          className={`admin-exit-signals-internal-tab ${
            selectedInternalTab === "execution-diagnostics" ? "active" : ""
          }`}
          onClick={() => setSelectedInternalTab("execution-diagnostics")}
        >
          Diagnostics
        </button>
      </div>

      {selectedInternalTab === "archived" && (
        <div>
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3>Cleanup Completed Positions</h3>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <input
                  type="number"
                  id="cleanup-days-input"
                  min="1"
                  max="365"
                  defaultValue="30"
                  style={{ width: "80px", padding: "0.25rem" }}
                />
                <label htmlFor="cleanup-days-input" style={{ fontSize: "0.9rem" }}>days old</label>
                <Button
                  onClick={() => {
                    const daysInput = document.getElementById("cleanup-days-input") as HTMLInputElement;
                    const daysOld = parseInt(daysInput?.value || "30", 10);
                    if (confirm(`Are you sure you want to delete all completed/closed positions older than ${daysOld} days? This will free up database space and reduce server load.`)) {
                      cleanupCompletedMutation.mutate(daysOld);
                    }
                  }}
                  variant="danger"
                  disabled={cleanupCompletedMutation.isPending}
                >
                  {cleanupCompletedMutation.isPending ? "Cleaning..." : "Cleanup Completed Positions"}
                </Button>
              </div>
            </div>
            <p style={{ color: "#666", fontSize: "0.9rem", marginTop: "0.5rem" }}>
              This will delete UserPosition entries with state=CLOSED and TradeLog entries with status='closed' 
              that are older than the specified number of days. This helps free up database space and reduce server load.
            </p>
          </Card>
          
          <Card style={{ marginTop: "1rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3>Comprehensive Cleanup (All Storage Locations)</h3>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                <input
                  type="number"
                  id="cleanup-all-days-input"
                  min="1"
                  max="365"
                  defaultValue="30"
                  style={{ width: "80px", padding: "0.25rem" }}
                />
                <label htmlFor="cleanup-all-days-input" style={{ fontSize: "0.9rem" }}>days old</label>
                <label style={{ fontSize: "0.9rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                  <input
                    type="checkbox"
                    id="include-pending-checkbox"
                    style={{ marginRight: "0.25rem" }}
                  />
                  Include pending/failed
                </label>
                <Button
                  onClick={() => {
                    const daysInput = document.getElementById("cleanup-all-days-input") as HTMLInputElement;
                    const includePendingCheckbox = document.getElementById("include-pending-checkbox") as HTMLInputElement;
                    const daysOld = parseInt(daysInput?.value || "30", 10);
                    const includePending = includePendingCheckbox?.checked || false;
                    if (confirm(
                      `Are you sure you want to delete ALL old data older than ${daysOld} days?\n\n` +
                      `This will clean up:\n` +
                      `- UserPosition (CLOSED)\n` +
                      `- TradeLog (closed)\n` +
                      `- ExecutionJob (succeeded${includePending ? "/failed/dead" : ""})\n` +
                      `- SignalEvent (archived)\n` +
                      `- ExitTicket (completed)\n` +
                      `- JobOutbox (published)\n` +
                      `- Redis Streams (old entries)\n\n` +
                      `This will free up significant database space and reduce server load.`
                    )) {
                      cleanupAllOldDataMutation.mutate({ daysOld, includePending });
                    }
                  }}
                  variant="danger"
                  disabled={cleanupAllOldDataMutation.isPending}
                >
                  {cleanupAllOldDataMutation.isPending ? "Cleaning..." : "Cleanup All Old Data"}
                </Button>
              </div>
            </div>
            <p style={{ color: "#666", fontSize: "0.9rem", marginTop: "0.5rem" }}>
              <strong>Comprehensive cleanup</strong> deletes old data from ALL storage locations:
              <br />• Database: UserPosition, TradeLog, ExecutionJob, SignalEvent, ExitTicket, JobOutbox
              <br />• Redis: Old stream entries
              <br />• This is the most thorough cleanup option and will free up the most space.
            </p>
          </Card>

          <Card>
            <h3>حذف الإشارات القديمة / Delete Old Exit Signals</h3>
            <p style={{ color: "#666", fontSize: "0.9rem", marginBottom: "1rem" }}>
              حذف الإشارات القديمة والصفقات المرتبطة بها. هذا يحذف الإشارات التي يفحصها النظام باستمرار.
              <br />
              Delete old exit signals and their positions. This removes signals that the system keeps checking.
            </p>
            <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
              <input
                id="delete-old-signals-hours-input"
                type="number"
                min="1"
                max="168"
                defaultValue={6}
                style={{ width: "80px", padding: "0.5rem" }}
              />
              <label htmlFor="delete-old-signals-hours-input" style={{ fontSize: "0.9rem" }}>hours old</label>
              <Button
                onClick={() => {
                  const maxAgeHours = parseInt(
                    (document.getElementById("delete-old-signals-hours-input") as HTMLInputElement)?.value || "6"
                  );
                  if (
                    confirm(
                      `هل أنت متأكد من حذف جميع الإشارات الأقدم من ${maxAgeHours} ساعة والصفقات المرتبطة بها؟\n\n` +
                      `Are you sure you want to delete all exit signals older than ${maxAgeHours} hours and their positions?\n\n` +
                      `سيتم حذف الصفقات من Redis (إيقاف المراقبة) والإشارات والـ jobs والـ DLQ messages.\n` +
                      `This will delete positions from Redis (stop monitoring), signals, jobs, and DLQ messages.`
                    )
                  ) {
                    deleteOldExitSignalsMutation.mutate(maxAgeHours);
                  }
                }}
                variant="danger"
                disabled={deleteOldExitSignalsMutation.isPending}
              >
                {deleteOldExitSignalsMutation.isPending ? "Deleting..." : "Delete Old Exit Signals"}
              </Button>
            </div>
            <p style={{ color: "#666", fontSize: "0.9rem", marginTop: "0.5rem" }}>
              <strong>حذف الإشارات القديمة:</strong>
              <br />• يبحث عن جميع الإشارات الأقدم من N ساعة
              <br />• يحذف جميع الصفقات المرتبطة بكل إشارة (يتوقف المراقبة)
              <br />• يحذف الإشارات والـ jobs والـ DLQ messages
              <br />• هذا يحل مشكلة الإشارات القديمة التي يفحصها النظام باستمرار
            </p>
          </Card>
          
          <ArchivedExitSignalsViewComponent />
          
          {/* Old Test Positions section - for cleaning up old test data */}
          <Card style={{ marginTop: "1.5rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3>Old Test Positions</h3>
              {oldTestPositions && oldTestPositions.total > 0 && (
                <Button
                  onClick={() => {
                    if (confirm(`Are you sure you want to delete all ${oldTestPositions.total} old test positions? This will permanently delete them from Redis, TradeLog, UserPosition, and Entry Signals.`)) {
                      deleteAllOldTestPositionsMutation.mutate();
                    }
                  }}
                  variant="danger"
                  disabled={deleteAllOldTestPositionsMutation.isPending}
                >
                  {deleteAllOldTestPositionsMutation.isPending ? "Deleting..." : "Delete All"}
                </Button>
              )}
            </div>
            {oldTestPositionsLoading ? (
              <Skeleton />
            ) : oldTestPositions && oldTestPositions.positions.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid var(--border)" }}>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Symbol</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Redis</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>TradeLog</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>UserPosition</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Entry Signal</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Age (Days)</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oldTestPositions.positions.map((pos) => (
                      <tr key={pos.symbol} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "0.75rem", fontWeight: "bold" }}>{pos.symbol}</td>
                        <td style={{ padding: "0.75rem" }}>
                          {pos.has_redis_position ? (
                            <span style={{ color: "var(--warning)", fontWeight: "bold" }}>
                              ✓ ({pos.redis_positions_count})
                            </span>
                          ) : (
                            <span style={{ color: "var(--text-secondary)" }}>✗</span>
                          )}
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          {pos.has_tradelog ? (
                            <span style={{ color: "var(--warning)", fontWeight: "bold" }}>
                              ✓ ({pos.tradelog_count})
                            </span>
                          ) : (
                            <span style={{ color: "var(--text-secondary)" }}>✗</span>
                          )}
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          {pos.has_userposition ? (
                            <span style={{ color: "var(--warning)", fontWeight: "bold" }}>
                              ✓ ({pos.userposition_count})
                            </span>
                          ) : (
                            <span style={{ color: "var(--text-secondary)" }}>✗</span>
                          )}
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          {pos.has_entry_signal ? (
                            <span style={{ color: "var(--danger)", fontWeight: "bold" }}>
                              ✓ {pos.entry_signal_id?.substring(0, 8)}...
                            </span>
                          ) : (
                            <span style={{ color: "var(--text-secondary)" }}>✗</span>
                          )}
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          {pos.entry_signal_age_days !== null && pos.entry_signal_age_days !== undefined ? (
                            <span>{pos.entry_signal_age_days}</span>
                          ) : (
                            <span style={{ color: "var(--text-secondary)" }}>N/A</span>
                          )}
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          <Button
                            onClick={() => {
                              if (confirm(`Are you sure you want to delete old test position ${pos.symbol}? This will permanently delete it from Redis, TradeLog, UserPosition, and Entry Signals.`)) {
                                deleteOldTestPositionMutation.mutate(pos.symbol);
                              }
                            }}
                            variant="danger"
                            disabled={deleteOldTestPositionMutation.isPending}
                            style={{ fontSize: "0.875rem", padding: "0.25rem 0.5rem" }}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="admin-exit-signals-empty">No old test positions found</div>
            )}
          </Card>
        </div>
      )}

      {selectedInternalTab === "cleanup-operations" && (
        <div>
          <Card>
            <h3 style={{ marginBottom: "1rem" }}>Operational Residue Cleanup (History Safe)</h3>
            <p style={{ color: "#666", fontSize: "0.9rem", marginBottom: "1rem" }}>
              This cleanup targets stale operational residue only: dead or failed jobs, stale signals,
              Redis residue, stream leftovers, and DLQ entries. It does <strong>not</strong> delete
              trading history from <strong>ExecutedTradeHistory</strong>, <strong>TradeLog</strong>, or
              <strong>UserPosition</strong>.
            </p>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", marginBottom: "1rem" }}>
              <input
                type="number"
                id="residue-max-age-hours"
                min="1"
                defaultValue="24"
                style={{ width: "90px", padding: "0.25rem" }}
              />
              <label htmlFor="residue-max-age-hours" style={{ fontSize: "0.9rem" }}>hours old</label>
              <input
                type="number"
                id="residue-stream-scan-count"
                min="100"
                max="20000"
                defaultValue="5000"
                style={{ width: "100px", padding: "0.25rem" }}
              />
              <label htmlFor="residue-stream-scan-count" style={{ fontSize: "0.9rem" }}>stream scan</label>
              <Button
                onClick={() => {
                  const maxAgeInput = document.getElementById("residue-max-age-hours") as HTMLInputElement;
                  const scanInput = document.getElementById("residue-stream-scan-count") as HTMLInputElement;
                  const maxAgeHours = parseInt(maxAgeInput?.value || "24", 10);
                  const streamScanCount = parseInt(scanInput?.value || "5000", 10);
                  previewResidueCleanupMutation.mutate({ maxAgeHours, streamScanCount });
                }}
                variant="secondary"
                disabled={previewResidueCleanupMutation.isPending || residueCleanupMutation.isPending}
              >
                {previewResidueCleanupMutation.isPending ? "Previewing..." : "Preview Safe Cleanup"}
              </Button>
              <Button
                onClick={() => {
                  const maxAgeInput = document.getElementById("residue-max-age-hours") as HTMLInputElement;
                  const scanInput = document.getElementById("residue-stream-scan-count") as HTMLInputElement;
                  const maxAgeHours = parseInt(maxAgeInput?.value || "24", 10);
                  const streamScanCount = parseInt(scanInput?.value || "5000", 10);
                  if (
                    confirm(
                      `Delete operational residue older than ${maxAgeHours} hours?\n\n` +
                        `This will clean jobs, stale signals, Redis residue, stream leftovers, and DLQ entries.\n` +
                        `Trading History tables will remain untouched.`
                    )
                  ) {
                    residueCleanupMutation.mutate({ maxAgeHours, streamScanCount });
                  }
                }}
                variant="primary"
                disabled={previewResidueCleanupMutation.isPending || residueCleanupMutation.isPending}
              >
                {residueCleanupMutation.isPending ? "Cleaning..." : "Execute Safe Cleanup"}
              </Button>
            </div>
            {residuePreviewData && (
              <div style={{ marginTop: "1rem", padding: "1rem", backgroundColor: "#eff6ff", borderRadius: "4px", border: "1px solid #93c5fd" }}>
                <h4 style={{ margin: "0 0 0.5rem 0", color: "#1d4ed8" }}>
                  Safe Cleanup Preview
                </h4>
                <div style={{ fontSize: "0.875rem", color: "#1e3a8a", marginBottom: "0.75rem" }}>
                  Protected tables: {(residuePreviewData.protected_tables || []).join(", ")}
                </div>
                {renderProtectedPositionsBreakdown(
                  (residuePreviewData.summary || {}) as Record<string, unknown>
                )}
                <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.8rem" }}>
                  {JSON.stringify(residuePreviewData.summary, null, 2)}
                </pre>
                <p style={{ marginTop: "0.75rem", fontSize: "0.875rem", color: "#1d4ed8", fontWeight: "bold" }}>
                  Total residue targeted: {residuePreviewData.total_targeted ?? 0}
                </p>
              </div>
            )}
            {residueCleanupError && (
              <div style={{ marginTop: "1rem", padding: "0.75rem", backgroundColor: "#fef2f2", borderRadius: "4px", border: "1px solid #fca5a5", color: "#dc2626" }}>
                Safe cleanup failed: {residueCleanupError}
              </div>
            )}
          </Card>

          {/* Comprehensive Cleanup with Preview */}
          <Card style={{ marginTop: "1.5rem" }}>
            <h3 style={{ marginBottom: "1rem" }}>Cleanup Operations</h3>
            <p style={{ color: "#666", fontSize: "0.9rem", marginBottom: "1rem" }}>
              Safe cleanup of old data. Use <strong>Preview</strong> first to see what would be deleted, 
              then use <strong>Execute</strong> to actually delete.
            </p>
            <div style={{ marginBottom: "1rem", padding: "0.75rem", borderRadius: "4px", backgroundColor: "#fff7ed", border: "1px solid #fdba74", color: "#9a3412", fontSize: "0.875rem" }}>
              Warning: this legacy cleanup can delete closed <strong>TradeLog</strong> and <strong>UserPosition</strong>
              records. If you need to preserve Trading History, use <strong>Operational Residue Cleanup</strong> above instead.
            </div>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", marginBottom: "1rem" }}>
              <label style={{ fontSize: "0.9rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                <input
                  type="checkbox"
                  id="cleanup-wipe-all"
                  style={{ marginRight: "0.25rem" }}
                />
                Full reset (delete everything)
              </label>
              <input
                type="number"
                id="cleanup-days-input"
                min="1"
                max="365"
                defaultValue="7"
                style={{ width: "80px", padding: "0.25rem" }}
              />
              <label htmlFor="cleanup-days-input" style={{ fontSize: "0.9rem" }}>days old</label>
              <label style={{ fontSize: "0.9rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                <input
                  type="checkbox"
                  id="cleanup-include-pending"
                  defaultChecked
                  style={{ marginRight: "0.25rem" }}
                />
                Include pending/failed
              </label>
              <Button
                onClick={() => {
                  const wipeAllCheckbox = document.getElementById("cleanup-wipe-all") as HTMLInputElement;
                  const daysInput = document.getElementById("cleanup-days-input") as HTMLInputElement;
                  const includePendingCheckbox = document.getElementById("cleanup-include-pending") as HTMLInputElement;
                  const wipeAll = wipeAllCheckbox?.checked ?? false;
                  const daysOld = parseInt(daysInput?.value || "7", 10);
                  const includePending = includePendingCheckbox?.checked ?? true;
                  previewCleanupMutation.mutate({ daysOld, includePending, wipeAll });
                }}
                variant="secondary"
                disabled={previewCleanupMutation.isPending || comprehensiveCleanupMutation.isPending}
              >
                {previewCleanupMutation.isPending ? "Previewing..." : "Preview"}
              </Button>
              <Button
                onClick={() => {
                  const wipeAllCheckbox = document.getElementById("cleanup-wipe-all") as HTMLInputElement;
                  const daysInput = document.getElementById("cleanup-days-input") as HTMLInputElement;
                  const includePendingCheckbox = document.getElementById("cleanup-include-pending") as HTMLInputElement;
                  const wipeAll = wipeAllCheckbox?.checked ?? false;
                  const daysOld = parseInt(daysInput?.value || "7", 10);
                  const includePending = includePendingCheckbox?.checked ?? true;
                  const msg = wipeAll
                    ? "Delete EVERYTHING from all tables and Redis? System will be as if first run."
                    : `Delete ALL old data older than ${daysOld} days from every table and Redis?`;
                  if (confirm(msg + "\n\nTables: trade_log, user_positions, signal_events, execution_jobs, job_outbox, order_intents, order_linkage, orphan_executions, exit_tickets, stuck_watchlist, exit_signal_watchlist + Redis positions.")) {
                    comprehensiveCleanupMutation.mutate({ daysOld, includePending, wipeAll });
                  }
                }}
                variant="danger"
                disabled={previewCleanupMutation.isPending || comprehensiveCleanupMutation.isPending}
              >
                {comprehensiveCleanupMutation.isPending ? "Cleaning..." : "Execute Cleanup"}
              </Button>
            </div>
            {previewData && (
              <div style={{ marginTop: "1rem", padding: "1rem", backgroundColor: "#f0fdf4", borderRadius: "4px", border: "1px solid #86efac" }}>
                <h4 style={{ margin: "0 0 0.5rem 0", color: "#166534" }}>
                  Preview Results (Dry Run - No data was deleted)
                </h4>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "0.5rem" }}>
                  {Object.entries(previewData.summary || {}).map(([key, value]) => {
                    if (key === "_preview") return null;
                    if (key === "redis" && typeof value === "object") {
                      return (
                        <div key={key} style={{ padding: "0.5rem", backgroundColor: "white", borderRadius: "4px" }}>
                          <strong style={{ fontSize: "0.875rem" }}>Redis:</strong>
                          <div style={{ fontSize: "0.875rem", color: "#166534" }}>
                            {(value as any).redis_positions_to_remove || 0} position(s)
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={key} style={{ padding: "0.5rem", backgroundColor: "white", borderRadius: "4px" }}>
                        <strong style={{ fontSize: "0.875rem" }}>{key.replace(/_/g, " ")}:</strong>
                        <div style={{ fontSize: "1.25rem", fontWeight: "bold", color: "#166534" }}>
                          {String(value)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p style={{ marginTop: "0.75rem", fontSize: "0.875rem", color: "#166534", fontWeight: "bold" }}>
                  Total records that would be deleted: {previewData.total_to_delete}
                </p>
                <p style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.25rem" }}>
                  Use "Execute Cleanup" above to actually delete these records.
                </p>
              </div>
            )}
            {previewCleanupError && (
              <div style={{ marginTop: "1rem", padding: "0.75rem", backgroundColor: "#fef2f2", borderRadius: "4px", border: "1px solid #fca5a5", color: "#dc2626" }}>
                Preview failed: {previewCleanupError}
              </div>
            )}
          </Card>

          {/* Redis Stream Management */}
          <Card style={{ marginTop: "1.5rem" }}>
            <h3 style={{ marginBottom: "1rem" }}>Redis Stream Management</h3>
            <p style={{ color: "#666", fontSize: "0.9rem", marginBottom: "1rem" }}>
              View and manage Redis streams (entry, exit, monitor, DLQ). Use Trim to safely remove old entries,
              or Purge for emergency deletion (destructive).
            </p>
            <RedisStreamManager />
          </Card>

          {/* Old Test Positions */}
          <Card style={{ marginTop: "1.5rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
              <h3>Old Test Positions</h3>
              {oldTestPositions && oldTestPositions.total > 0 && (
                <Button
                  onClick={() => {
                    if (confirm(`Are you sure you want to delete all ${oldTestPositions.total} old test positions?`)) {
                      deleteAllOldTestPositionsMutation.mutate();
                    }
                  }}
                  variant="danger"
                  disabled={deleteAllOldTestPositionsMutation.isPending}
                >
                  {deleteAllOldTestPositionsMutation.isPending ? "Deleting..." : "Delete All"}
                </Button>
              )}
            </div>
            {oldTestPositionsLoading ? (
              <Skeleton />
            ) : oldTestPositions && oldTestPositions.positions.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid var(--border)" }}>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Symbol</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Redis</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>TradeLog</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>UserPosition</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Entry Signal</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Age (Days)</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oldTestPositions.positions.map((pos) => (
                      <tr key={pos.symbol} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "0.75rem", fontWeight: "bold" }}>{pos.symbol}</td>
                        <td style={{ padding: "0.75rem" }}>
                          {pos.has_redis_position ? (
                            <span style={{ color: "var(--warning)", fontWeight: "bold" }}>
                              ✓ ({pos.redis_positions_count})
                            </span>
                          ) : (
                            <span style={{ color: "var(--text-secondary)" }}>✗</span>
                          )}
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          {pos.has_tradelog ? (
                            <span style={{ color: "var(--warning)", fontWeight: "bold" }}>
                              ✓ ({pos.tradelog_count})
                            </span>
                          ) : (
                            <span style={{ color: "var(--text-secondary)" }}>✗</span>
                          )}
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          {pos.has_userposition ? (
                            <span style={{ color: "var(--warning)", fontWeight: "bold" }}>
                              ✓ ({pos.userposition_count})
                            </span>
                          ) : (
                            <span style={{ color: "var(--text-secondary)" }}>✗</span>
                          )}
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          {pos.has_entry_signal ? (
                            <span style={{ color: "var(--danger)", fontWeight: "bold" }}>
                              ✓ {pos.entry_signal_id?.substring(0, 8)}...
                            </span>
                          ) : (
                            <span style={{ color: "var(--text-secondary)" }}>✗</span>
                          )}
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          {pos.entry_signal_age_days !== null && pos.entry_signal_age_days !== undefined ? (
                            <span>{pos.entry_signal_age_days}</span>
                          ) : (
                            <span style={{ color: "var(--text-secondary)" }}>N/A</span>
                          )}
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          <Button
                            onClick={() => {
                              if (confirm(`Are you sure you want to delete old test position ${pos.symbol}?`)) {
                                deleteOldTestPositionMutation.mutate(pos.symbol);
                              }
                            }}
                            variant="danger"
                            disabled={deleteOldTestPositionMutation.isPending}
                            style={{ fontSize: "0.875rem", padding: "0.25rem 0.5rem" }}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="admin-exit-signals-empty" style={{ marginTop: "0.5rem" }}>No old test positions found</div>
            )}
          </Card>
        </div>
      )}

      {selectedInternalTab === "stuck-jobs" && (
        <div className="admin-exit-signals-stuck-jobs-section">
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <div>
                <h3>All Stuck Positions</h3>
                {allStuckPositions && (
                  <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginTop: "0.5rem" }}>
                    Total: {allStuckPositions.total} | 
                    Exit Signals: {allStuckPositions.by_source.exit_signal} | 
                    Stuck Jobs: {allStuckPositions.by_source.stuck_job} | 
                    Pending Orders: {allStuckPositions.by_source.pending_order}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                {allStuckPositions && allStuckPositions.total > 0 && (
                  <Button
                    onClick={() => {
                      const stuckJobCount = allStuckPositions.by_source.stuck_job || 0;
                      const pendingOrderCount = allStuckPositions.by_source.pending_order || 0;
                      
                      if (confirm(
                        `Are you sure you want to delete all stuck positions?\n\n` +
                        `Stuck Jobs: ${stuckJobCount}\n` +
                        `Pending Orders: ${pendingOrderCount}\n\n` +
                        `Total: ${allStuckPositions.total}`
                      )) {
                        // Delete stuck jobs
                        if (stuckJobCount > 0) {
                          deleteAllStuckJobsMutation.mutate();
                        }
                        // Cancel pending orders
                        if (pendingOrderCount > 0) {
                          cancelAllPendingOrdersMutation.mutate();
                        }
                      }
                    }}
                    variant="danger"
                    disabled={deleteAllStuckJobsMutation.isPending || cancelAllPendingOrdersMutation.isPending}
                  >
                    {(deleteAllStuckJobsMutation.isPending || cancelAllPendingOrdersMutation.isPending)
                      ? "Deleting..."
                      : "Delete All"}
                  </Button>
                )}
                {stuckJobs && stuckJobs.total > 0 && (
                  <Button
                    onClick={() => {
                      if (confirm(`Are you sure you want to delete all ${stuckJobs.total} stuck jobs?`)) {
                        deleteAllStuckJobsMutation.mutate();
                      }
                    }}
                    variant="danger"
                    disabled={deleteAllStuckJobsMutation.isPending}
                  >
                    {deleteAllStuckJobsMutation.isPending ? "Deleting..." : "Delete All Stuck Jobs"}
                  </Button>
                )}
                <Button
                  onClick={() => {
                    if (confirm(
                      `Are you sure you want to delete ALL pending jobs from database?\n\n` +
                      `This will delete jobs with status: queued, pending, running, failed (old), dead\n\n` +
                      `This action cannot be undone!`
                    )) {
                      deleteAllPendingJobsMutation.mutate();
                    }
                  }}
                  variant="danger"
                  disabled={deleteAllPendingJobsMutation.isPending}
                  style={{ backgroundColor: "var(--danger)", color: "white" }}
                >
                  {deleteAllPendingJobsMutation.isPending ? "Deleting..." : "Delete All Pending Jobs"}
                </Button>
              </div>
            </div>
            {allStuckPositionsLoading ? (
              <Skeleton />
            ) : allStuckPositions && allStuckPositions.stuck_positions.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid var(--border)" }}>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Source</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>User</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Symbol</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Side</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Status</th>
                      <th style={{ padding: "0.75rem", textAlign: "right" }}>Quantity</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>ID</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Error</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Created</th>
                      <th style={{ padding: "0.75rem", textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allStuckPositions.stuck_positions.map((pos, index) => {
                      const identifier = pos.position_id || pos.job_id || pos.order_id || `index-${index}`;
                      
                      return (
                      <tr key={`${pos.source}-${identifier}`} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "0.75rem" }}>
                          <span style={{ 
                            padding: "0.25rem 0.5rem", 
                            borderRadius: "4px", 
                            fontSize: "0.75rem",
                            backgroundColor: pos.source === "exit_signal" ? "var(--warning)" : 
                                           pos.source === "stuck_job" ? "var(--info)" : "var(--text-secondary)",
                            color: "white"
                          }}>
                            {pos.source}
                          </span>
                        </td>
                        <td style={{ padding: "0.75rem" }}>
                          {pos.user_email || pos.user_name || pos.user_id || "N/A"}
                        </td>
                        <td style={{ padding: "0.75rem" }}>{pos.symbol || "N/A"}</td>
                        <td style={{ padding: "0.75rem" }}>{pos.side || "N/A"}</td>
                        <td style={{ padding: "0.75rem" }}>{pos.status || "N/A"}</td>
                        <td style={{ padding: "0.75rem", textAlign: "right" }}>
                          {pos.quantity ? pos.quantity.toFixed(8) : "N/A"}
                        </td>
                        <td style={{ padding: "0.75rem", fontSize: "0.875rem", fontFamily: "monospace" }}>
                          {pos.position_id ? `${pos.position_id.substring(0, 8)}...` :
                           pos.job_id ? `${pos.job_id.substring(0, 8)}...` :
                           pos.order_id ? `${pos.order_id.substring(0, 12)}...` : "N/A"}
                        </td>
                        <td style={{ padding: "0.75rem", fontSize: "0.875rem", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {pos.error_message ? pos.error_message.substring(0, 50) + "..." : 
                           pos.error_code || "N/A"}
                        </td>
                        <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                          {pos.created_at ? new Date(pos.created_at).toLocaleString("en-US") : "N/A"}
                        </td>
                        <td style={{ padding: "0.75rem", textAlign: "center" }}>
                          {pos.source === "stuck_job" && pos.job_id ? (
                            <>
                              <Button
                                onClick={() => {
                                  if (confirm(`Are you sure you want to delete stuck job ${pos.job_id} for ${pos.symbol || "N/A"}?`)) {
                                    deleteStuckJobMutation.mutate(pos.job_id!);
                                  }
                                }}
                                variant="danger"
                                size="small"
                                disabled={deleteStuckJobMutation.isPending}
                                style={{ marginRight: "0.5rem" }}
                              >
                                Delete
                              </Button>
                              <Button
                                onClick={() => {
                                  if (confirm(`Are you sure you want to delete job ${pos.job_id} directly from database?`)) {
                                    deleteJobByIdMutation.mutate(pos.job_id!);
                                  }
                                }}
                                variant="danger"
                                size="small"
                                disabled={deleteJobByIdMutation.isPending}
                              >
                                Delete DB
                              </Button>
                            </>
                          ) : pos.source === "pending_order" && pos.order_id ? (
                            <Button
                              onClick={() => {
                                if (confirm(`Are you sure you want to cancel pending order ${pos.order_id} for ${pos.symbol || "N/A"}?`)) {
                                  cancelPendingOrderMutation.mutate({
                                    order_id: pos.order_id!,
                                    symbol: pos.symbol || "",
                                    account_id: pos.account_id || "",
                                  });
                                }
                              }}
                              variant="danger"
                              size="small"
                              disabled={cancelPendingOrderMutation.isPending}
                            >
                              Cancel
                            </Button>
                          ) : (
                            <span style={{ color: "var(--text-secondary)", fontSize: "0.875rem" }}>N/A</span>
                          )}
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="admin-exit-signals-empty">
                No stuck positions found.
              </div>
            )}
          </Card>

          {/* All Pending Jobs Section */}
          <Card style={{ marginTop: "1rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <div>
                <h3>All Pending Jobs (From Database)</h3>
                {allPendingJobs && (
                  <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)", marginTop: "0.5rem" }}>
                    Total: {allPendingJobs.total} jobs (queued, pending, running, failed old, dead)
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                {allPendingJobs && allPendingJobs.total > 0 && (
                  <Button
                    onClick={() => {
                      if (confirm(
                        `Are you sure you want to delete ALL ${allPendingJobs.total} pending jobs from database?\n\n` +
                        `This will delete jobs with status: queued, pending, running, failed (old), dead\n\n` +
                        `This action cannot be undone!`
                      )) {
                        deleteAllPendingJobsMutation.mutate();
                      }
                    }}
                    variant="danger"
                    disabled={deleteAllPendingJobsMutation.isPending}
                    style={{ backgroundColor: "var(--danger)", color: "white" }}
                  >
                    {deleteAllPendingJobsMutation.isPending ? "Deleting..." : "Delete All Pending Jobs"}
                  </Button>
                )}
              </div>
            </div>
            {allPendingJobsLoading ? (
              <Skeleton />
            ) : allPendingJobs && allPendingJobs.jobs.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid var(--border)" }}>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Job ID</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Type</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Status</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>User ID</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Symbol</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Signal ID</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Attempts</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Created</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Error</th>
                      <th style={{ padding: "0.75rem", textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allPendingJobs.jobs.map((job) => (
                      <tr key={job.job_id} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "0.75rem", fontSize: "0.875rem", fontFamily: "monospace" }}>
                          {job.job_id.substring(0, 12)}...
                        </td>
                        <td style={{ padding: "0.75rem" }}>{job.job_type}</td>
                        <td style={{ padding: "0.75rem" }}>
                          <span style={{ 
                            padding: "0.25rem 0.5rem", 
                            borderRadius: "0.25rem", 
                            backgroundColor: job.status === "running" ? "var(--info-light, rgba(0, 123, 255, 0.1))" :
                                           job.status === "failed" ? "var(--error-light, rgba(220, 53, 69, 0.1))" :
                                           job.status === "dead" ? "var(--error-light, rgba(220, 53, 69, 0.1))" :
                                           "var(--warning-light, rgba(255, 193, 7, 0.1))",
                            color: job.status === "running" ? "var(--info, #007bff)" :
                                   job.status === "failed" ? "var(--error, #dc3545)" :
                                   job.status === "dead" ? "var(--error, #dc3545)" :
                                   "var(--warning, #ffc107)",
                            fontSize: "0.875rem"
                          }}>
                            {job.status}
                          </span>
                        </td>
                        <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>{job.user_id || "N/A"}</td>
                        <td style={{ padding: "0.75rem" }}>{job.symbol || "N/A"}</td>
                        <td style={{ padding: "0.75rem", fontSize: "0.875rem", fontFamily: "monospace" }}>
                          {job.signal_id ? `${job.signal_id.substring(0, 8)}...` : "N/A"}
                        </td>
                        <td style={{ padding: "0.75rem" }}>{job.attempts}/{job.max_attempts}</td>
                        <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                          {new Date(job.created_at).toLocaleString("en-US")}
                        </td>
                        <td style={{ padding: "0.75rem", fontSize: "0.875rem", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {job.last_error ? job.last_error.substring(0, 50) + "..." : "N/A"}
                        </td>
                        <td style={{ padding: "0.75rem", textAlign: "center" }}>
                          <Button
                            onClick={() => {
                              if (confirm(`Are you sure you want to delete job ${job.job_id} directly from database?`)) {
                                deleteJobByIdMutation.mutate(job.job_id);
                              }
                            }}
                            variant="danger"
                            size="small"
                            disabled={deleteJobByIdMutation.isPending}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="admin-exit-signals-empty">
                No pending jobs found in database.
              </div>
            )}
          </Card>
        </div>
      )}

      {selectedInternalTab === "pending-orders" && (
        <div className="admin-exit-signals-pending-orders-section">
          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3>Pending Orders</h3>
              {pendingOrders && pendingOrders.total > 0 && (
                <Button
                  onClick={() => {
                    if (confirm(`Are you sure you want to cancel all ${pendingOrders.total} pending orders?`)) {
                      cancelAllPendingOrdersMutation.mutate();
                    }
                  }}
                  variant="danger"
                  disabled={cancelAllPendingOrdersMutation.isPending}
                >
                  {cancelAllPendingOrdersMutation.isPending ? "Cancelling..." : "Cancel All"}
                </Button>
              )}
            </div>
            {pendingOrdersLoading ? (
              <Skeleton />
            ) : pendingOrders && pendingOrders.pending_orders.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid var(--border)" }}>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Symbol</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Side</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Type</th>
                      <th style={{ padding: "0.75rem", textAlign: "right" }}>Price</th>
                      <th style={{ padding: "0.75rem", textAlign: "right" }}>Amount</th>
                      <th style={{ padding: "0.75rem", textAlign: "right" }}>Filled</th>
                      <th style={{ padding: "0.75rem", textAlign: "right" }}>Remaining</th>
                      <th style={{ padding: "0.75rem", textAlign: "right" }}>Age (min)</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Order ID</th>
                      <th style={{ padding: "0.75rem", textAlign: "left" }}>Account ID</th>
                      <th style={{ padding: "0.75rem", textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingOrders.pending_orders.map((order) => (
                      <tr key={order.order_id} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "0.75rem" }}>{order.symbol}</td>
                        <td style={{ padding: "0.75rem" }}>{order.side}</td>
                        <td style={{ padding: "0.75rem" }}>{order.type}</td>
                        <td style={{ padding: "0.75rem", textAlign: "right" }}>
                          {order.price === "MARKET" ? "MARKET" : typeof order.price === "number" ? `$${order.price.toFixed(4)}` : order.price}
                        </td>
                        <td style={{ padding: "0.75rem", textAlign: "right" }}>
                          {order.amount.toFixed(8)}
                        </td>
                        <td style={{ padding: "0.75rem", textAlign: "right" }}>
                          {order.filled.toFixed(8)}
                        </td>
                        <td style={{ padding: "0.75rem", textAlign: "right" }}>
                          {order.remaining.toFixed(8)}
                        </td>
                        <td style={{ padding: "0.75rem", textAlign: "right" }}>
                          {order.age_minutes.toFixed(2)}
                        </td>
                        <td style={{ padding: "0.75rem", fontSize: "0.875rem", fontFamily: "monospace" }}>
                          {order.order_id.substring(0, 12)}...
                        </td>
                        <td style={{ padding: "0.75rem", fontSize: "0.875rem", fontFamily: "monospace" }}>
                          {order.account_id.substring(0, 12)}...
                        </td>
                        <td style={{ padding: "0.75rem", textAlign: "center" }}>
                          <Button
                            onClick={() => {
                              if (confirm(`Are you sure you want to cancel order ${order.order_id} for ${order.symbol}?`)) {
                                cancelPendingOrderMutation.mutate({
                                  order_id: order.order_id,
                                  symbol: order.symbol,
                                  account_id: order.account_id,
                                });
                              }
                            }}
                            variant="danger"
                            size="small"
                            disabled={cancelPendingOrderMutation.isPending}
                          >
                            Cancel
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="admin-exit-signals-empty">
                No pending orders found.
              </div>
            )}
          </Card>
        </div>
      )}

      {selectedInternalTab === "positions-monitor" && (
        <AdminPositionsMonitorTab />
      )}

      {selectedInternalTab === "subscriber-positions" && (
        <SubscriberPositionsTab />
      )}

      {selectedInternalTab === "execution-diagnostics" && (
        <ExecutionDiagnosticsTab />
      )}

      {selectedInternalTab === "signals" && (
        <>
      <Card className="admin-exit-signals-summary">
        <div className="admin-exit-signals-summary-grid">
          <div className="admin-exit-signals-summary-item">
            <span className="admin-exit-signals-summary-label">Total Signals</span>
            <span className="admin-exit-signals-summary-value">{recentSignals.total}</span>
          </div>
          <div className="admin-exit-signals-summary-item admin-exit-signals-summary-item--warning">
            <span className="admin-exit-signals-summary-label">Signals with Open Positions</span>
            <span className="admin-exit-signals-summary-value">
              {signalsWithOpenPositions.length}
            </span>
          </div>
        </div>
      </Card>

      <Card style={{ marginTop: "1rem", marginBottom: "1rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
          <h3 style={{ margin: 0 }}>Archive Stuck Exit Signals</h3>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button
              onClick={() => {
                if (confirm(
                  "Run dry-run to preview what would be archived?\n\n" +
                  "This will show stuck exit signals with EXIT_NOT_FOUND errors without making changes."
                )) {
                  archiveStuckExitSignalsMutation.mutate({
                    dry_run: true,
                    delete_execution_jobs: false,
                  });
                }
              }}
              variant="secondary"
              disabled={archiveStuckExitSignalsMutation.isPending}
            >
              {archiveStuckExitSignalsMutation.isPending ? "Running..." : "Dry Run"}
            </Button>
            <Button
              onClick={() => {
                const symbols = prompt(
                  "Enter symbols to archive (comma-separated, e.g., WET/USDT,ANKR/USDT,POL/USDT) or leave empty for all:\n\n" +
                  "This will archive exit signals with EXIT_NOT_FOUND errors and delete their ExecutionJobs."
                );
                if (symbols !== null) {
                  const symbolsList = symbols.trim()
                    ? symbols.split(",").map(s => s.trim()).filter(s => s.length > 0)
                    : undefined;
                  
                  if (confirm(
                    `Are you sure you want to archive stuck exit signals${symbolsList ? ` for symbols: ${symbolsList.join(", ")}` : " (all symbols)"}?\n\n` +
                    "This will:\n" +
                    "1. Archive exit signals with EXIT_NOT_FOUND errors\n" +
                    "2. Delete ExecutionJobs with EXIT_NOT_FOUND errors\n" +
                    "3. Prevent the system from retrying these signals\n\n" +
                    "This action cannot be undone."
                  )) {
                    archiveStuckExitSignalsMutation.mutate({
                      symbols: symbolsList,
                      dry_run: false,
                      delete_execution_jobs: true,
                    });
                  }
                }
              }}
              variant="danger"
              disabled={archiveStuckExitSignalsMutation.isPending}
            >
              {archiveStuckExitSignalsMutation.isPending ? "Archiving..." : "Archive Stuck Signals"}
            </Button>
          </div>
        </div>
        <p style={{ color: "#666", fontSize: "0.9rem", margin: 0 }}>
          Archive exit signals that have ExecutionJobs stuck in DLQ with EXIT_NOT_FOUND errors. 
          This prevents the system from continuously retrying these signals. 
          Optionally specify symbols to filter (e.g., WET/USDT, ANKR/USDT) or leave empty to archive all stuck signals.
        </p>
      </Card>

      <Card style={{ marginTop: "1rem", marginBottom: "1rem", border: "2px solid var(--error)", backgroundColor: "#fff5f5" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, color: "var(--error)", marginBottom: "0.5rem" }}>
              🗑️ حذف الصفقات المعلقة من جذورها
            </h3>
            <h4 style={{ margin: "0.5rem 0", color: "#333", fontSize: "1rem" }}>
              Delete Stuck Positions Completely
            </h4>
            <div style={{ marginTop: "0.75rem", padding: "0.75rem", backgroundColor: "#fff", borderRadius: "4px", border: "1px solid #ddd" }}>
              <p style={{ margin: "0 0 0.5rem 0", color: "#333", fontSize: "0.9rem", fontWeight: "bold" }}>
                ما هي هذه الخدمة؟
              </p>
              <p style={{ margin: "0 0 0.75rem 0", color: "#666", fontSize: "0.85rem", lineHeight: "1.5" }}>
                هذه الخدمة تحذف الصفقات المعلقة (Stuck Positions) من <strong>جميع</strong> الأماكن في النظام بشكل كامل.
                تستخدم نفس منطق السكريبت <code>delete_stuck_positions.py</code> لحذف البيانات من:
              </p>
              <ul style={{ margin: "0 0 0.75rem 0", paddingLeft: "1.5rem", color: "#666", fontSize: "0.85rem", lineHeight: "1.6" }}>
                <li><strong>قاعدة البيانات:</strong> ExecutionJob, SignalEvent (مؤرشف), UserPosition, TradeLog, ExitTicket</li>
                <li><strong>Redis:</strong> positions cache, exit_stream, DLQ</li>
                <li><strong>أي بيانات أخرى مرتبطة</strong></li>
              </ul>
              <p style={{ margin: "0", color: "#666", fontSize: "0.85rem", lineHeight: "1.5" }}>
                بعد الحذف، النظام <strong>لن يحاول</strong> معالجة هذه الصفقات مرة أخرى.
              </p>
            </div>
            <div style={{ marginTop: "0.75rem", padding: "0.75rem", backgroundColor: "#fff3cd", borderRadius: "4px", border: "1px solid #ffc107" }}>
              <p style={{ margin: "0 0 0.5rem 0", color: "#856404", fontSize: "0.85rem", fontWeight: "bold" }}>
                كيفية الاستخدام:
              </p>
              <ol style={{ margin: "0", paddingLeft: "1.5rem", color: "#856404", fontSize: "0.85rem", lineHeight: "1.6" }}>
                <li>أدخل <strong>واحد على الأقل</strong> من: Job ID, Signal ID, Symbol, أو User Email</li>
                <li>استخدم <strong>"Dry Run Delete"</strong> لمعاينة ما سيتم حذفه بدون تنفيذ</li>
                <li>استخدم <strong>"Delete Completely"</strong> للحذف النهائي</li>
              </ol>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", flexWrap: "wrap" }}>
          <Button
            onClick={() => {
              const jobId = prompt("أدخل Job ID (مثال: 01KH67TKMJB8QMENAMVTM5BJQG):\n\nEnter Job ID (e.g., 01KH67TKMJB8QMENAMVTM5BJQG):");
              const signalId = prompt("أدخل Signal ID (مثال: 4a082226-4b0d-40e4-a28f-8c4b61988f53) أو اتركه فارغاً:\n\nEnter Signal ID (e.g., 4a082226-4b0d-40e4-a28f-8c4b61988f53) or leave empty:");
              const symbol = prompt("أدخل Symbol (مثال: WET/USDT) أو اتركه فارغاً:\n\nEnter Symbol (e.g., WET/USDT) or leave empty:");
              const userEmail = prompt("أدخل User Email أو اتركه فارغاً:\n\nEnter User Email or leave empty:");
              
              if (jobId || signalId || symbol) {
                if (confirm(
                  `🔍 Dry Run - معاينة الحذف\n\n` +
                  `Job ID: ${jobId || "N/A"}\n` +
                  `Signal ID: ${signalId || "N/A"}\n` +
                  `Symbol: ${symbol || "N/A"}\n` +
                  `User Email: ${userEmail || "N/A"}\n\n` +
                  `سيتم عرض جميع البيانات التي سيتم حذفها بدون تنفيذ أي تغييرات.\n\n` +
                  `This will show all data that would be deleted without making changes.`
                )) {
                  deleteStuckPositionMutation.mutate({
                    job_id: jobId || undefined,
                    signal_id: signalId || undefined,
                    symbol: symbol || undefined,
                    user_email: userEmail || undefined,
                    dry_run: true,
                  });
                }
              } else {
                alert("يرجى إدخال واحد على الأقل: Job ID, Signal ID, أو Symbol\n\nPlease provide at least one identifier: Job ID, Signal ID, or Symbol");
              }
            }}
            variant="secondary"
            disabled={deleteStuckPositionMutation.isPending}
            style={{ minWidth: "150px" }}
          >
            {deleteStuckPositionMutation.isPending ? "جاري التشغيل..." : "🔍 Dry Run Delete"}
          </Button>
          <Button
            onClick={() => {
              const jobId = prompt("أدخل Job ID (مثال: 01KH67TKMJB8QMENAMVTM5BJQG):\n\nEnter Job ID (e.g., 01KH67TKMJB8QMENAMVTM5BJQG):");
              const signalId = prompt("أدخل Signal ID (مثال: 4a082226-4b0d-40e4-a28f-8c4b61988f53) أو اتركه فارغاً:\n\nEnter Signal ID (e.g., 4a082226-4b0d-40e4-a28f-8c4b61988f53) or leave empty:");
              const symbol = prompt("أدخل Symbol (مثال: WET/USDT) أو اتركه فارغاً:\n\nEnter Symbol (e.g., WET/USDT) or leave empty:");
              const userEmail = prompt("أدخل User Email أو اتركه فارغاً:\n\nEnter User Email or leave empty:");
              
              if (jobId || signalId || symbol) {
                if (confirm(
                  `⚠️ تحذير: هل أنت متأكد من حذف هذه الصفقة المعلقة بشكل كامل؟\n\n` +
                  `⚠️ DANGER: Are you sure you want to COMPLETELY DELETE this stuck position?\n\n` +
                  `Job ID: ${jobId || "N/A"}\n` +
                  `Signal ID: ${signalId || "N/A"}\n` +
                  `Symbol: ${symbol || "N/A"}\n` +
                  `User Email: ${userEmail || "N/A"}\n\n` +
                  `سيتم الحذف من جميع الأماكن:\n` +
                  `This will DELETE from ALL storage locations:\n` +
                  `1. ExecutionJob\n` +
                  `2. SignalEvent (مؤرشف / archived)\n` +
                  `3. UserPosition\n` +
                  `4. TradeLog\n` +
                  `5. ExitTicket\n` +
                  `6. Redis positions cache\n` +
                  `7. Redis streams (exit_stream)\n` +
                  `8. Redis DLQ\n` +
                  `9. أي بيانات أخرى مرتبطة\n` +
                  `9. Any other related data\n\n` +
                  `⚠️ هذا الإجراء لا يمكن التراجع عنه!\n` +
                  `⚠️ This action CANNOT be undone!\n\n` +
                  `بعد الحذف، النظام لن يحاول معالجة هذه الصفقة مرة أخرى.\n` +
                  `The system will stop trying to process this position completely.`
                )) {
                  deleteStuckPositionMutation.mutate({
                    job_id: jobId || undefined,
                    signal_id: signalId || undefined,
                    symbol: symbol || undefined,
                    user_email: userEmail || undefined,
                    dry_run: false,
                  });
                }
              } else {
                alert("يرجى إدخال واحد على الأقل: Job ID, Signal ID, أو Symbol\n\nPlease provide at least one identifier: Job ID, Signal ID, or Symbol");
              }
            }}
            variant="danger"
            disabled={deleteStuckPositionMutation.isPending}
            style={{ minWidth: "150px" }}
          >
            {deleteStuckPositionMutation.isPending ? "جاري الحذف..." : "🗑️ Delete Completely"}
          </Button>
        </div>
        <div style={{ marginTop: "1rem", padding: "0.75rem", backgroundColor: "#f8d7da", borderRadius: "4px", border: "1px solid #f5c6cb" }}>
          <p style={{ margin: 0, color: "#721c24", fontSize: "0.85rem", lineHeight: "1.5" }}>
            <strong>⚠️ منطقة الخطر:</strong> هذه الخدمة تحذف البيانات بشكل دائم ولا يمكن التراجع عنها. 
            استخدم "Dry Run Delete" أولاً لمعاينة ما سيتم حذفه قبل التنفيذ النهائي.
            <br />
            <strong>⚠️ DANGER ZONE:</strong> This service permanently deletes data and cannot be undone. 
            Use "Dry Run Delete" first to preview what will be deleted before final execution.
          </p>
        </div>
      </Card>

      {signalsWithOpenPositions.length === 0 ? (
        <Card>
          <div className="admin-exit-signals-empty">
            No exit signals with open positions found. All subscribers have exited successfully.
          </div>
        </Card>
      ) : (
        <div className="admin-exit-signals-list">
          {signalsWithOpenPositions.map((signal) => (
            <Card key={signal.signal_id} className="admin-exit-signals-signal-card">
              <div className="admin-exit-signals-signal-header">
                <div>
                  <h3>
                    {signal.symbol} ({signal.side})
                  </h3>
                  <p className="admin-exit-signals-signal-id">Signal ID: {signal.signal_id}</p>
                  {signal.dispatched_at && (
                    <p className="admin-exit-signals-signal-time">
                      Dispatched: {new Date(signal.dispatched_at).toLocaleString("en-US")}
                    </p>
                  )}
                </div>
                <div className="admin-exit-signals-signal-actions">
                  <Button
                    onClick={() => {
                      setSelectedSignalId(
                        selectedSignalId === signal.signal_id ? null : signal.signal_id
                      );
                      // CRITICAL FIX: useEffect will handle refetch when selectedSignalId changes
                      // No need to call refetchStatus() here
                    }}
                    variant="secondary"
                  >
                    {selectedSignalId === signal.signal_id ? "Hide Details" : "View Details"}
                  </Button>
                </div>
              </div>

              <div className="admin-exit-signals-signal-stats">
                <div className="admin-exit-signals-stat">
                  <span className="admin-exit-signals-stat-label">Total Users</span>
                  <span className="admin-exit-signals-stat-value">{signal.total_users}</span>
                </div>
                <div className="admin-exit-signals-stat admin-exit-signals-stat--success">
                  <span className="admin-exit-signals-stat-label">Succeeded</span>
                  <span className="admin-exit-signals-stat-value">{signal.succeeded}</span>
                </div>
                <div className="admin-exit-signals-stat admin-exit-signals-stat--failed">
                  <span className="admin-exit-signals-stat-label">Failed</span>
                  <span className="admin-exit-signals-stat-value">{signal.failed}</span>
                </div>
                <div className="admin-exit-signals-stat admin-exit-signals-stat--warning">
                  <span className="admin-exit-signals-stat-label">Queued</span>
                  <span className="admin-exit-signals-stat-value">{signal.queued}</span>
                </div>
                <div className="admin-exit-signals-stat admin-exit-signals-stat--info">
                  <span className="admin-exit-signals-stat-label">Running</span>
                  <span className="admin-exit-signals-stat-value">{signal.running}</span>
                </div>
                <div className="admin-exit-signals-stat admin-exit-signals-stat--error">
                  <span className="admin-exit-signals-stat-label">Open Positions</span>
                  <span className="admin-exit-signals-stat-value">
                    {signal.users_with_open_positions.length}
                  </span>
                </div>
              </div>

              {selectedSignalId === signal.signal_id && (
                <div className="admin-exit-signals-signal-details">
                  {statusLoading ? (
                    <Skeleton />
                  ) : signalStatus ? (
                    <>
                      {signalStatus.users_with_open_positions.length > 0 && (
                        <div className="admin-exit-signals-users-list">
                          <h4>Users with Open Positions:</h4>
                          <ul>
                            {signalStatus.users_with_open_positions_details && signalStatus.users_with_open_positions_details.length > 0 ? (
                              signalStatus.users_with_open_positions_details.map((userDetail: any, index: number) => (
                                <li key={`${userDetail.user_id}-${index}-${userDetail.position_id || ''}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem", borderBottom: "1px solid var(--border)" }}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", flex: 1 }}>
                                    <span style={{ fontWeight: "bold" }}>
                                      {userDetail.user_email || userDetail.user_name || userDetail.user_id}
                                    </span>
                                    {userDetail.user_email && userDetail.user_email !== userDetail.user_id && (
                                      <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                                        {userDetail.user_id}
                                      </span>
                                    )}
                                    {userDetail.position_id && (
                                      <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)", fontFamily: "monospace" }}>
                                        Position: {userDetail.position_id.substring(0, 12)}...
                                      </span>
                                    )}
                                    {userDetail.error_code && (
                                      <span style={{ fontSize: "0.75rem", color: "var(--error)" }}>
                                        Error: {userDetail.error_code}
                                      </span>
                                    )}
                                    {userDetail.error_message && (
                                      <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>
                                        {userDetail.error_message.substring(0, 100)}
                                        {userDetail.error_message.length > 100 ? "..." : ""}
                                      </span>
                                    )}
                                  </div>
                                  <Button
                                    onClick={() => {
                                      if (confirm(
                                        `هل أنت متأكد من حذف صفقة المستخدم ${userDetail.user_email || userDetail.user_name || userDetail.user_id}?\n\n` +
                                        `Are you sure you want to delete position for user ${userDetail.user_email || userDetail.user_name || userDetail.user_id}?\n\n` +
                                        `Signal: ${signal.symbol} (${signal.side})\n` +
                                        `User ID: ${userDetail.user_id}\n` +
                                        `Position ID: ${userDetail.position_id || "N/A"}\n\n` +
                                        `سيتم حذف الصفقة من Redis, TradeLog, UserPosition, و DLQ.\n` +
                                        `This will delete the position from Redis, TradeLog, UserPosition, and DLQ.`
                                      )) {
                                        deletePositionBySignalMutation.mutate({
                                          signalId: signal.signal_id,
                                          request: {
                                            user_ids: [userDetail.user_id],
                                            reason: "admin_manual_deletion_from_exit_signals_tab",
                                          },
                                        });
                                      }
                                    }}
                                    variant="danger"
                                    size="small"
                                    disabled={deletePositionBySignalMutation.isPending}
                                    style={{ marginLeft: "0.5rem", fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                                  >
                                    {deletePositionBySignalMutation.isPending ? "Deleting..." : "Delete"}
                                  </Button>
                                </li>
                              ))
                            ) : (
                              signalStatus.users_with_open_positions.map((userId) => (
                                <li key={userId}>{userId}</li>
                              ))
                            )}
                          </ul>
                        </div>
                      )}

                      <div className="admin-exit-signals-actions">
                        <Button
                          onClick={() => setShowRetryConfirm(signal.signal_id)}
                          variant="secondary"
                          disabled={retryMutation.isPending}
                        >
                          {retryMutation.isPending ? "Retrying..." : "Retry Exit Jobs"}
                        </Button>
                        <Button
                          onClick={() => setShowForceCloseConfirm(signal.signal_id)}
                          variant="danger"
                          disabled={forceCloseMutation.isPending}
                        >
                          {forceCloseMutation.isPending
                            ? "Force Closing..."
                            : "Force Close Positions (Last Resort)"}
                        </Button>
                      </div>

                      {showRetryConfirm === signal.signal_id && (
                        <div className="admin-exit-signals-confirm">
                          <p>Are you sure you want to retry exit jobs for this signal?</p>
                          <div className="admin-exit-signals-confirm-actions">
                            <Button
                              onClick={() => {
                                retryMutation.mutate({
                                  signalId: signal.signal_id,
                                  request: {},
                                });
                              }}
                              variant="secondary"
                            >
                              Yes, Retry
                            </Button>
                            <Button
                              onClick={() => setShowRetryConfirm(null)}
                              variant="secondary"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}

                      {showForceCloseConfirm === signal.signal_id && (
                        <div className="admin-exit-signals-confirm admin-exit-signals-confirm--danger">
                          <p>
                            <strong>Warning:</strong> This will force close positions directly on
                            the exchange. This is a LAST RESORT option. Are you sure?
                          </p>
                          <div className="admin-exit-signals-confirm-actions">
                            <Button
                              onClick={() => {
                                forceCloseMutation.mutate({
                                  signalId: signal.signal_id,
                                  request: {
                                    reason: "admin_force_close_after_exit_failure",
                                  },
                                });
                              }}
                              variant="danger"
                            >
                              Yes, Force Close
                            </Button>
                            <Button
                              onClick={() => setShowForceCloseConfirm(null)}
                              variant="secondary"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="admin-exit-signals-error">Failed to load signal details</div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      {/* Pending Signals section - signals that haven't been dispatched yet */}
      <Card style={{ marginTop: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h3>Pending Signals (Not Yet Dispatched)</h3>
          {pendingSignals && pendingSignals.total > 0 && (
            <Button
              onClick={() => {
                if (confirm(`Are you sure you want to delete all ${pendingSignals.total} pending signals? These are signals that haven't been dispatched and ExitJobFallbackService keeps trying to process.`)) {
                  deleteAllPendingSignalsMutation.mutate();
                }
              }}
              variant="danger"
              disabled={deleteAllPendingSignalsMutation.isPending}
            >
              {deleteAllPendingSignalsMutation.isPending ? "Deleting..." : "Delete All"}
            </Button>
          )}
        </div>
        {pendingSignalsLoading ? (
          <Skeleton />
        ) : pendingSignals && pendingSignals.signals.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "2px solid var(--border)" }}>
                  <th style={{ padding: "0.75rem", textAlign: "left" }}>Signal ID</th>
                  <th style={{ padding: "0.75rem", textAlign: "left" }}>Symbol</th>
                  <th style={{ padding: "0.75rem", textAlign: "left" }}>Action</th>
                  <th style={{ padding: "0.75rem", textAlign: "left" }}>Side</th>
                  <th style={{ padding: "0.75rem", textAlign: "left" }}>Status</th>
                  <th style={{ padding: "0.75rem", textAlign: "left" }}>Created At</th>
                  <th style={{ padding: "0.75rem", textAlign: "left" }}>Age (Hours)</th>
                  <th style={{ padding: "0.75rem", textAlign: "left" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingSignals.signals.map((signal) => (
                  <tr key={signal.signal_id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "0.75rem", fontSize: "0.875rem", fontFamily: "monospace" }}>
                      {signal.signal_id.substring(0, 8)}...
                    </td>
                    <td style={{ padding: "0.75rem" }}>{signal.symbol}</td>
                    <td style={{ padding: "0.75rem" }}>{signal.action}</td>
                    <td style={{ padding: "0.75rem" }}>{signal.side}</td>
                    <td style={{ padding: "0.75rem" }}>
                      <span style={{ 
                        padding: "0.25rem 0.5rem", 
                        borderRadius: "0.25rem", 
                        backgroundColor: "var(--warning-light, rgba(255, 193, 7, 0.1))",
                        color: "var(--warning, #ffc107)",
                        fontSize: "0.875rem"
                      }}>
                        {signal.status}
                      </span>
                    </td>
                    <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                      {new Date(signal.created_at).toLocaleString("en-US")}
                    </td>
                    <td style={{ padding: "0.75rem" }}>
                      {signal.age_hours.toFixed(1)}
                    </td>
                    <td style={{ padding: "0.75rem" }}>
                      <Button
                        onClick={() => {
                          if (confirm(`Are you sure you want to delete pending signal ${signal.signal_id.substring(0, 8)}... for ${signal.symbol}?`)) {
                            deletePendingSignalMutation.mutate(signal.signal_id);
                          }
                        }}
                        variant="danger"
                        disabled={deletePendingSignalMutation.isPending}
                        style={{ fontSize: "0.875rem", padding: "0.25rem 0.5rem" }}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-exit-signals-empty">No pending signals found</div>
        )}
      </Card>
        </>
      )}
    </div>
  );
}

// Archived Exit Signals View Component
function ArchivedExitSignalsViewComponent(): JSX.Element {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    symbol: "",
    status: "",
    closed_reason: "",
    start_date: "",
    end_date: "",
  });
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [dryRunResults, setDryRunResults] = useState<ArchiveExitSignalsResponse | null>(null);

  const {
    data: archivedData,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["admin", "exit-signals", "archived", filters, page],
    queryFn: () =>
      getArchivedExitSignals({
        ...filters,
        page,
        page_size: pageSize,
      }),
    enabled: true,
  });

  const dryRunArchiveMutation = useMutation({
    mutationFn: (request: ArchiveExitSignalsRequest = {}) =>
      archiveExitSignals({ ...request, dry_run: true }),
    onSuccess: (data) => {
      setDryRunResults(data); // حفظ النتائج للعرض في الجدول
      const sampleIds = data.signals_to_archive
        ?.slice(0, 5)
        .map((s: any) => s.signal_id?.substring(0, 8) || s.signal_id)
        .join(", ") || "none";
      alert(
        `Dry run completed: ${data.can_archive} signal(s) would be archived (out of ${data.total_found} found). ` +
        `Cannot archive: ${data.cannot_archive}. Sample IDs: ${sampleIds}`
      );
    },
    onError: (error: any) => {
      logger.error("Failed to dry run archive", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to dry run archive: ${error.message || "Unknown error"}`);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: ({ request, closeActiveTickets }: { request: ArchiveExitSignalsRequest; closeActiveTickets?: boolean }) =>
      closeActiveTickets
        ? archiveExitSignalsWithForce({ ...request, dry_run: false }, true)
        : archiveExitSignals({ ...request, dry_run: false }),
    onSuccess: (data) => {
      setDryRunResults(null); // مسح نتائج Dry Run بعد الأرشفة الفعلية
      queryClient.invalidateQueries({ queryKey: ["admin", "exit-signals", "archived"] });
      refetch();
      
      // عرض أسباب عدم إمكانية الأرشفة
      let reasonsMessage = "";
      if (data.cannot_archive > 0 && data.signals_cannot_archive && data.signals_cannot_archive.length > 0) {
        // جمع الأسباب من أول 5 إشارات
        const reasons = new Set<string>();
        data.signals_cannot_archive.slice(0, 5).forEach((signal: any) => {
          if (signal.validation && signal.validation.reasons) {
            signal.validation.reasons.forEach((reason: string) => reasons.add(reason));
          }
        });
        if (reasons.size > 0) {
          reasonsMessage = `\n\nReasons: ${Array.from(reasons).join(", ")}`;
        }
      }
      
      alert(
        `Successfully archived ${data.archived} signal(s) (out of ${data.total_found} found). ` +
        `Cannot archive: ${data.cannot_archive}${reasonsMessage}`
      );
    },
    onError: (error: any) => {
      logger.error("Failed to archive signals", error instanceof Error ? error : new Error(String(error)));
      alert(`Failed to archive signals: ${error.message || "Unknown error"}`);
    },
  });

  return (
    <div className="admin-exit-signals-archived-section">
      <Card>
        <div style={{ marginBottom: "1rem" }}>
          <h3>Archived Exit Signals</h3>
          <p style={{ color: "var(--text-secondary)", fontSize: "0.875rem" }}>
            View archived/stale exit signals that have been soft-closed
          </p>
        </div>

        {/* Filters */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
          <div>
            <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
              Symbol
            </label>
            <input
              type="text"
              value={filters.symbol}
              onChange={(e) => setFilters({ ...filters, symbol: e.target.value })}
              placeholder="e.g., SUI/USDT"
              style={{ width: "100%", padding: "0.5rem" }}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              style={{ width: "100%", padding: "0.5rem" }}
            >
              <option value="">All</option>
              <option value="ARCHIVED">Archived</option>
              <option value="CLOSED_STALE">Closed Stale</option>
            </select>
          </div>
          <div>
            <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
              Closed Reason
            </label>
            <input
              type="text"
              value={filters.closed_reason}
              onChange={(e) => setFilters({ ...filters, closed_reason: e.target.value })}
              placeholder="e.g., stale_cleanup"
              style={{ width: "100%", padding: "0.5rem" }}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
              Start Date
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              style={{ width: "100%", padding: "0.5rem" }}
            />
          </div>
          <div>
            <label style={{ display: "block", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
              End Date
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              style={{ width: "100%", padding: "0.5rem" }}
            />
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
          <Button
            onClick={() => {
              if (confirm("Run dry-run archive? This will show what would be archived without making changes.")) {
                dryRunArchiveMutation.mutate({});
              }
            }}
            variant="secondary"
            disabled={dryRunArchiveMutation.isPending}
          >
            {dryRunArchiveMutation.isPending ? "Running..." : "Dry Run Archive"}
          </Button>
          <Button
            onClick={() => {
              if (confirm("Archive stale exit signals? This will soft-close signals older than 7 days with no open positions.")) {
                archiveMutation.mutate({ request: {} });
              }
            }}
            variant="primary"
            disabled={archiveMutation.isPending}
          >
            {archiveMutation.isPending ? "Archiving..." : "Archive Stale Signals"}
          </Button>
          <Button
            onClick={() => {
              if (confirm("Archive stale exit signals and close active exit tickets? This will close active tickets before archiving.")) {
                archiveMutation.mutate({ request: {}, closeActiveTickets: true });
              }
            }}
            variant="primary"
            disabled={archiveMutation.isPending}
          >
            {archiveMutation.isPending ? "Archiving..." : "Archive & Close Tickets"}
          </Button>
        </div>

        {/* Dry Run Results Summary */}
        {dryRunResults && (
          <div style={{ 
            marginBottom: "1rem", 
            padding: "1rem", 
            backgroundColor: "var(--warning-light, rgba(255, 193, 7, 0.1))", 
            border: "1px solid var(--warning, #ffc107)",
            borderRadius: "0.5rem"
          }}>
            <h4 style={{ marginTop: 0, marginBottom: "0.5rem", color: "var(--warning, #ffc107)" }}>
              Dry Run Results (Preview)
            </h4>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.5rem", fontSize: "0.875rem" }}>
              <div><strong>Total Found:</strong> {dryRunResults.total_found}</div>
              <div><strong>Can Archive:</strong> {dryRunResults.can_archive}</div>
              <div><strong>Cannot Archive:</strong> {dryRunResults.cannot_archive}</div>
            </div>
            <p style={{ fontSize: "0.875rem", marginTop: "0.5rem", marginBottom: 0, color: "var(--text-secondary)" }}>
              Click "Archive Stale Signals" to archive these signals. Results below show signals that would be archived.
            </p>
          </div>
        )}

        {/* Table */}
        {isLoading ? (
          <Skeleton />
        ) : isError ? (
          <div className="admin-exit-signals-error">Failed to load archived signals</div>
        ) : (dryRunResults && dryRunResults.signals_to_archive && dryRunResults.signals_to_archive.length > 0) ? (
          <>
            {/* Show Dry Run Results */}
            <div style={{ marginBottom: "1rem", fontSize: "0.875rem", color: "var(--text-secondary)" }}>
              Showing {dryRunResults.signals_to_archive.length} signal(s) that would be archived (Dry Run preview)
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    <th style={{ padding: "0.75rem", textAlign: "left" }}>Signal ID</th>
                    <th style={{ padding: "0.75rem", textAlign: "left" }}>Symbol</th>
                    <th style={{ padding: "0.75rem", textAlign: "left" }}>Dispatched At</th>
                    <th style={{ padding: "0.75rem", textAlign: "left" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dryRunResults.signals_to_archive.map((signal: any, index: number) => (
                    <tr key={signal.signal_id || index} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "0.75rem", fontSize: "0.875rem", fontFamily: "monospace" }}>
                        {signal.signal_id?.substring(0, 8) || signal.signal_id}...
                      </td>
                      <td style={{ padding: "0.75rem" }}>{signal.symbol}</td>
                      <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                        {signal.dispatched_at ? new Date(signal.dispatched_at).toLocaleString("en-US") : "N/A"}
                      </td>
                      <td style={{ padding: "0.75rem" }}>
                        <span style={{ 
                          padding: "0.25rem 0.5rem", 
                          borderRadius: "0.25rem", 
                          backgroundColor: "var(--success-light, rgba(40, 167, 69, 0.1))",
                          color: "var(--success, #28a745)",
                          fontSize: "0.875rem"
                        }}>
                          Ready to Archive
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : archivedData && archivedData.items && archivedData.items.length > 0 ? (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    <th style={{ padding: "0.75rem", textAlign: "left" }}>Signal ID</th>
                    <th style={{ padding: "0.75rem", textAlign: "left" }}>Symbol</th>
                    <th style={{ padding: "0.75rem", textAlign: "left" }}>Side</th>
                    <th style={{ padding: "0.75rem", textAlign: "left" }}>Status</th>
                    <th style={{ padding: "0.75rem", textAlign: "left" }}>Closed Reason</th>
                    <th style={{ padding: "0.75rem", textAlign: "left" }}>Dispatched At</th>
                    <th style={{ padding: "0.75rem", textAlign: "left" }}>Archived At</th>
                  </tr>
                </thead>
                <tbody>
                  {archivedData.items.map((signal) => (
                    <tr key={signal.signal_id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "0.75rem", fontSize: "0.875rem", fontFamily: "monospace" }}>
                        {signal.signal_id.substring(0, 8)}...
                      </td>
                      <td style={{ padding: "0.75rem" }}>{signal.symbol}</td>
                      <td style={{ padding: "0.75rem" }}>{signal.side}</td>
                      <td style={{ padding: "0.75rem" }}>{signal.status}</td>
                      <td style={{ padding: "0.75rem" }}>{signal.closed_reason || "N/A"}</td>
                      <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                        {signal.dispatched_at ? new Date(signal.dispatched_at).toLocaleString("en-US") : "N/A"}
                      </td>
                      <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                        {signal.archived_at ? new Date(signal.archived_at).toLocaleString("en-US") : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {archivedData.total > pageSize && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1rem" }}>
                <div style={{ fontSize: "0.875rem", color: "var(--text-secondary)" }}>
                  Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, archivedData.total)} of {archivedData.total}
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <Button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    variant="secondary"
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page * pageSize >= archivedData.total}
                    variant="secondary"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="admin-exit-signals-empty">No archived signals found</div>
        )}
      </Card>
    </div>
  );
}

